<template>
    <div class="d-md-flex mt_26 cost_total_col align-items-start">
        <div class="cost-breakdown-container ml-auto">
            <div class="cost-row">
                <span class="cost-label">Quantity</span>
                <span class="cost-value">{{ quantity }}</span>
            </div>
            <div class="cost-row">
                <div class="tooltip-wrapper" @click.stop="toggleTooltip">
                    <span class="cost-label">{{
                        getRollupFeeDisplayName(1) || "Base Fee"
                    }}</span>

                    <svg
                        class="info-icon-svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 5.33203V8.66536"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 10.668H8.00599"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div
                        v-if="baseCost > 0 || getBaseFeeServices().length > 0"
                        class="tooltip-content"
                        :class="{ 'is-visible': tooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>
                                {{ getRollupFeeDisplayName(1) || "Base Fee" }}
                                Breakdown
                            </h4>
                            <svg
                                class="close-icon"
                                @click.stop="closeTooltip"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Color</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Color-Size Combinations -->
                                    <template
                                        v-if="
                                            colorSizeCombinations &&
                                            colorSizeCombinations.length > 0
                                        "
                                    >
                                        <tr
                                            v-for="(
                                                combination, index
                                            ) in colorSizeCombinations"
                                            :key="'color-size-' + index"
                                        >
                                            <td>
                                                Product Base Price -
                                                {{ combination.size || "N/A" }}
                                            </td>
                                            <td>
                                                <div class="color-box-wrapper">
                                                    <div
                                                        class="color-box"
                                                        :style="{
                                                            backgroundColor:
                                                                getColorValue(
                                                                    combination
                                                                )
                                                        }"
                                                        :title="
                                                            getColorName(
                                                                combination
                                                            )
                                                        "
                                                    ></div>
                                                </div>
                                            </td>
                                            <td>
                                                {{ combination.quantity || 0 }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getColorSizeUnitPrice(
                                                            combination
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getColorSizeTotalPrice(
                                                            combination
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 1 for Base Fee) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            1
                                        )"
                                    >
                                        <!-- Services included in Product Base Price (show_service = false) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td>1</td>
                                            <td>{{ quantity }}</td>
                                            <td>INCL</td>
                                            <td>INCL</td>
                                        </tr>

                                        <!-- Services shown as separate lines (show_service = true) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Product Base Price (Total) - shown at end after services -->
                                    <tr
                                        v-if="baseCost > 0"
                                        class="product-base-price-row d-none"
                                    >
                                        <td>
                                            <strong>Product Base Price</strong>
                                        </td>
                                        <td>-</td>
                                        <td v-if="quantity > 0">
                                            <strong>{{ quantity }}</strong>
                                        </td>
                                        <td v-else><strong>-</strong></td>
                                        <td>
                                            <strong>{{
                                                quantity > 0
                                                    ? formatPrice(
                                                          getBaseUnitPrice()
                                                      )
                                                    : formatPrice(baseCost)
                                            }}</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    getProductBasePriceTotal()
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>

                                    <!-- Total Base Fee -->
                                    <tr class="total-row">
                                        <td colspan="4">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(getTooltipTotal)
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <span class="cost-value">{{
                    formatPrice(displayBaseCost)
                }}</span>
            </div>
            <div class="cost-row" v-if="showRunChargeCostRow">
                <div
                    class="tooltip-wrapper"
                    @click.stop="toggleRunChargeTooltip"
                    v-if="hasRunChargeData()"
                >
                    <span class="cost-label">{{
                        getRollupFeeDisplayName(12) || "Run Charge"
                    }}</span>
                    <svg
                        class="info-icon-svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 5.33203V8.66536"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 10.668H8.00599"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': runChargeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>
                                {{
                                    getRollupFeeDisplayName(12) || "Run Charge"
                                }}
                                Breakdown
                            </h4>
                            <svg
                                class="close-icon"
                                @click.stop="closeRunChargeTooltip"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- 1st Location -->
                                    <tr v-if="getFirstLocationData()">
                                        <td>
                                            <strong
                                                >1st Location{{
                                                    getFirstLocationName() !==
                                                    "1st Location"
                                                        ? " (" +
                                                          getFirstLocationName() +
                                                          ")"
                                                        : ""
                                                }}</strong
                                            >
                                        </td>
                                        <td>{{ quantity }}</td>
                                        <td
                                            v-if="
                                                getServiceMasterData(
                                                    'location_1st'
                                                ) > 0
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getServiceMasterData(
                                                        'location_1st'
                                                    )
                                                )
                                            }}
                                        </td>
                                        <td v-else>INCL</td>
                                        <td
                                            v-if="
                                                getServiceMasterData(
                                                    'location_1st'
                                                ) > 0
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getServiceMasterData(
                                                        'location_1st'
                                                    ) * quantity
                                                )
                                            }}
                                        </td>
                                        <td v-else>INCL</td>
                                    </tr>
                                    <!-- 1st Color or Full Color -->
                                    <tr
                                        v-if="
                                            getFirstLocationData() &&
                                            ((decorationType !== 2 &&
                                                decorationType !== 4) ||
                                                (isFirstLocationFullColor() &&
                                                    getFirstLocationFullColorValue() >
                                                        0) ||
                                                (!isFirstLocationFullColor() &&
                                                    getServiceMasterData(
                                                        'cost_per_color_1st'
                                                    ) > 0))
                                        "
                                    >
                                        <td>
                                            <span
                                                v-if="
                                                    isFirstLocationFullColor()
                                                "
                                                >Full Color</span
                                            >
                                            <span v-else>1st Color</span>
                                        </td>
                                        <td>{{ quantity }}</td>
                                        <td
                                            v-if="
                                                isFirstLocationFullColor() &&
                                                getFirstLocationFullColorValue() >
                                                    0
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getFirstLocationFullColorValue()
                                                )
                                            }}
                                        </td>
                                        <td
                                            v-else-if="
                                                !isFirstLocationFullColor() &&
                                                getServiceMasterData(
                                                    'cost_per_color_1st'
                                                ) > 0
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getServiceMasterData(
                                                        'cost_per_color_1st'
                                                    )
                                                )
                                            }}
                                        </td>
                                        <td v-else>INCL</td>
                                        <td
                                            v-if="
                                                isFirstLocationFullColor() &&
                                                getFirstLocationFullColorValue() >
                                                    0
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getFirstLocationFullColorValue() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                        <td
                                            v-else-if="
                                                !isFirstLocationFullColor() &&
                                                getServiceMasterData(
                                                    'cost_per_color_1st'
                                                ) > 0
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getServiceMasterData(
                                                        'cost_per_color_1st'
                                                    ) * quantity
                                                )
                                            }}
                                        </td>
                                        <td v-else>INCL</td>
                                    </tr>
                                    <!-- Addl Color for Location 1 (if location1Colors > 1) -->
                                    <tr
                                        v-if="
                                            getFirstLocationAdditionalColors() >
                                                0 &&
                                            getFirstLocationAddlColorPrice() > 0
                                        "
                                    >
                                        <td>
                                            Addl Color ({{
                                                getFirstLocationAdditionalColors()
                                            }})
                                        </td>
                                        <td>
                                            {{
                                                getLocation1AdditionalColorUnits()
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationAddlColorPrice()
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationAddlColorPrice() *
                                                        getLocation1AdditionalColorUnits()
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Specialty Color Upcharge for 1st Location -->
                                    <tr
                                        v-if="
                                            getFirstLocationColorUpcharge() > 0
                                        "
                                    >
                                        <td>Specialty Color</td>
                                        <td>{{ quantity }}</td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationColorUpcharge()
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationColorUpcharge() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <!-- Custom Color Upcharge for 1st Location -->
                                    <tr
                                        v-if="
                                            getFirstLocationCustomColorUpcharge() >
                                            0
                                        "
                                    >
                                        <td>Custom Color</td>
                                        <td>{{ quantity }}</td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationCustomColorUpcharge()
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getFirstLocationCustomColorUpcharge() *
                                                        quantity
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Name/Number Fees for 1st Location (Front) -->
                                    <tr
                                        v-for="(
                                            fee, feeIdx
                                        ) in getFirstLocationNameNumberFees()"
                                        :key="'fee-1st-' + feeIdx"
                                    >
                                        <td>{{ fee.name }}</td>
                                        <td>{{ fee.quantity || quantity }}</td>
                                        <td>
                                            {{ formatPrice(fee.perItemFee) }}
                                        </td>
                                        <td>{{ formatPrice(fee.total) }}</td>
                                    </tr>

                                    <!-- Additional Locations (2nd, 3rd, 4th, etc.) -->
                                    <template
                                        v-for="(
                                            row, rowIndex
                                        ) in additionalLocationRows"
                                    >
                                        <tr
                                            v-if="
                                                row.type === 'location' ||
                                                row.type === 'addlColor' ||
                                                row.type === 'fullColor' ||
                                                row.type === 'nameNumberFee' ||
                                                row.type === 'colorUpcharge' ||
                                                row.type ===
                                                    'customColorUpcharge'
                                            "
                                            :key="
                                                'loc-' +
                                                row.locationIndex +
                                                '-' +
                                                row.type +
                                                '-' +
                                                rowIndex
                                            "
                                        >
                                            <td>
                                                <span
                                                    v-if="
                                                        row.type === 'location'
                                                    "
                                                    ><strong
                                                        >Additional Location{{
                                                            row.locationName
                                                                ? " (" +
                                                                  row.locationName +
                                                                  ")"
                                                                : ""
                                                        }}</strong
                                                    ></span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type === 'addlColor'
                                                    "
                                                    >Addl Color{{
                                                        row.colorCount
                                                            ? " (" +
                                                              row.colorCount +
                                                              ")"
                                                            : ""
                                                    }}</span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type === 'fullColor'
                                                    "
                                                    >Full Color</span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type ===
                                                        'nameNumberFee'
                                                    "
                                                    >{{ row.name }}</span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type ===
                                                        'colorUpcharge'
                                                    "
                                                    >Specialty Color</span
                                                >
                                                <span
                                                    v-else-if="
                                                        row.type ===
                                                        'customColorUpcharge'
                                                    "
                                                    >Custom Color</span
                                                >
                                            </td>
                                            <td v-if="row.type === 'location'">
                                                {{ row.qty }}
                                            </td>
                                            <td v-else>{{ row.qty }}</td>
                                            <td v-if="row.type === 'location'">
                                                <span v-if="row.isIncluded"
                                                    >-</span
                                                >
                                                <span v-else>{{
                                                    formatPrice(row.amt)
                                                }}</span>
                                            </td>
                                            <td v-else-if="row.isIncluded">
                                                INCL
                                            </td>
                                            <td v-else>
                                                {{ formatPrice(row.amt) }}
                                            </td>
                                            <td v-if="row.type === 'location'">
                                                <span v-if="row.isIncluded"
                                                    >-</span
                                                >
                                                <span v-else>{{
                                                    formatPrice(row.total)
                                                }}</span>
                                            </td>
                                            <td v-else-if="row.isIncluded">
                                                INCL
                                            </td>
                                            <td v-else>
                                                {{ formatPrice(row.total) }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 12 for Run Charge) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            12
                                        )"
                                    >
                                        <!-- Services with show_service = false (grayed out, but show price) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>

                                        <!-- Services with show_service = true (normal display) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Other Run Charge Details (excluding location, color mix, name/number fees) -->
                                    <tr
                                        v-for="(
                                            detail, detailIdx
                                        ) in getOtherRunChargeDetails()"
                                        :key="'other-detail-' + detailIdx"
                                    >
                                        <td>{{ detail.name }}</td>
                                        <td>
                                            {{
                                                detail.quantity != null
                                                    ? detail.quantity
                                                    : detail.perItem
                                                    ? quantity
                                                    : "-"
                                            }}
                                        </td>
                                        <td v-if="detail.unitPrice != null">
                                            {{ formatPrice(detail.unitPrice) }}
                                        </td>
                                        <td v-else-if="detail.perItem">
                                            {{ formatPrice(detail.perItemFee) }}
                                        </td>
                                        <td v-else>
                                            {{ formatPrice(detail.value) }}
                                        </td>
                                        <td>{{ formatPrice(detail.value) }}</td>
                                    </tr>
                                    <!-- Total Run Charge -->
                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedRunChargeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <span class="cost-label" v-else>
                    {{ getRollupFeeDisplayName(12) || "Run Charge" }}
                </span>

                <span class="cost-value">{{
                    formatPrice(calculatedRunChargeTotal)
                }}</span>
            </div>
            <div class="cost-row" v-if="calculatedColorMixFeeTotal > 0">
                <div
                    class="tooltip-wrapper"
                    @click.stop="toggleColorMixFeeTooltip"
                    v-if="hasColorMixFeeData()"
                >
                    <span class="cost-label">
                        <span
                            v-if="isEmbroideryService() || decorationType === 2 || decorationType === 4"
                            >{{
                                getRollupFeeDisplayName(13) ||
                                "Custom Color Fee"
                            }}</span
                        >
                        <span
                            v-else-if="
                                decorationType === 1 || decorationType === 5
                            "
                            >{{
                            getRollupFeeDisplayName(13) || "Custom Color Fee"
                        }}</span>
                        <span v-else>{{
                            getRollupFeeDisplayName(13) || "Custom Color Fee"
                        }}</span>
                    </span>
                    <svg
                        class="info-icon-svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 5.33203V8.66536"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 10.668H8.00599"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': colorMixFeeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>
                                <span
                                    v-if="
                                        isEmbroideryService() ||
                                        decorationType === 2 ||
                                        decorationType === 4
                                    "
                                    >{{
                                        getRollupFeeDisplayName(13) ||
                                        "Custom Color Fee"
                                    }}
                                    Breakdown</span
                                >
                                <span
                                    v-else-if="
                                        decorationType === 1 ||
                                        decorationType === 5
                                    "
                                    >{{
                                        getRollupFeeDisplayName(13) ||
                                        "Custom Color Fee"
                                    }}
                                    Breakdown</span
                                >
                                <span v-else
                                    >{{
                                        getRollupFeeDisplayName(13) ||
                                        "Custom Color Fee"
                                    }}
                                    Breakdown</span
                                >
                            </h4>
                            <svg
                                class="close-icon"
                                @click.stop="closeColorMixFeeTooltip"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-if="
                                            getOrderMixFeeData() &&
                                            getOrderMixFeeData().total > 0
                                        "
                                    >
                                        <td>
                                            <strong>
                                                <span
                                                    v-if="
                                                        isEmbroideryService() ||
                                                        decorationType === 2 ||
                                                        decorationType === 4
                                                    "
                                                    >Custom Color Thread
                                                    Fee</span
                                                >
                                                <span v-else
                                                    >Custom Color Ink Fee</span
                                                >
                                            </strong>
                                        </td>
                                        <td>
                                            {{
                                                getOrderMixFeeData()
                                                    .mixColorCount
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getOrderMixFeeData()
                                                        .unitPrice
                                                )
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getOrderMixFeeData().total
                                                )
                                            }}
                                        </td>
                                    </tr>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 13 for Custom Color Fee) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            13
                                        )"
                                    >
                                        <!-- Services with show_service = false (grayed out, but show price) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>

                                        <!-- Services with show_service = true (normal display) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>-</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedColorMixFeeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <span class="cost-label" v-else>
                    <span v-if="isEmbroideryService() || decorationType === 2 || decorationType === 4"
                        >Custom Color Thread Fee</span
                    >
                    <span
                        v-else-if="
                            decorationType === 1 || decorationType === 5
                        "
                        >Custom Color Ink Fee</span
                    >
                    <span v-else>{{
                        getRollupFeeDisplayName(13) || "Custom Color Fee"
                    }}</span>
                </span>
                <span class="cost-value">{{
                    formatPrice(calculatedColorMixFeeTotal)
                }}</span>
            </div>
            <div class="cost-row" v-if="calculatedSetupFeeTotal > 0">
                <div
                    class="tooltip-wrapper"
                    @click.stop="toggleSetupFeeTooltip"
                    v-if="hasSetupFeeData()"
                >
                    <span class="cost-label">{{
                        getRollupFeeDisplayName(2) || "Setup Fee"
                    }}</span>
                    <svg
                        class="info-icon-svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 5.33203V8.66536"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 10.668H8.00599"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': setupFeeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>
                                {{ getRollupFeeDisplayName(2) || "Setup Fee" }}
                                Breakdown
                            </h4>
                            <svg
                                class="close-icon"
                                @click.stop="closeSetupFeeTooltip"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Fee</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Setup Fee Location 1 -->
                                    <tr
                                        v-if="
                                            getFirstLocationData() &&
                                            getFirstLocationColorCount() > 0
                                        "
                                    >
                                        <td>
                                            <strong
                                                >{{
                                                    getRollupFeeDisplayName(
                                                        2
                                                    ) || "Set Up Fee"
                                                }}
                                                Location 1{{
                                                    getFirstLocationName() !==
                                                    "1st Location"
                                                        ? " (" +
                                                          getFirstLocationName() +
                                                          ")"
                                                        : ""
                                                }}</strong
                                            >
                                        </td>
                                        <td
                                            v-if="
                                                getSetupFeeStructure() == '1' ||
                                                getSetupFeeStructure() === '1'
                                            "
                                        >
                                            {{ getFirstLocationColorCount() }}
                                        </td>
                                        <td v-else>1</td>
                                        <td
                                            v-if="
                                                getSetupFeeStructure() == '1' ||
                                                getSetupFeeStructure() === '1'
                                            "
                                        >
                                            {{
                                                formatPrice(
                                                    getSetupFeeMasterData(
                                                        "setup_fee_color1"
                                                    )
                                                )
                                            }}
                                        </td>
                                        <td v-else>
                                            <span
                                                v-if="
                                                    isFirstLocationFullColor() ||
                                                    getFirstLocationColorCount() >
                                                        1
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getSetupFeeMasterData(
                                                            "setup_fee_multicolor"
                                                        )
                                                    )
                                                }}
                                            </span>
                                            <span v-else>
                                                {{
                                                    formatPrice(
                                                        getSetupFeeMasterData(
                                                            "setup_fee_color1"
                                                        )
                                                    )
                                                }}
                                            </span>
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    getLocation1SetupFee()
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <!-- Setup Fee for Additional Locations -->
                                    <template
                                        v-for="(
                                            location, idx
                                        ) in getAdditionalLocations()"
                                    >
                                        <tr
                                            v-if="
                                                location.isFullColor === true ||
                                                location.colorCount > 0
                                            "
                                            :key="'setup-loc-' + location.index"
                                        >
                                            <td>
                                                <strong
                                                    >{{
                                                        getRollupFeeDisplayName(
                                                            2
                                                        ) || "Set Up Fee"
                                                    }}
                                                    Location {{ idx + 2
                                                    }}{{
                                                        location.locationName
                                                            ? " (" +
                                                              location.locationName +
                                                              ")"
                                                            : ""
                                                    }}</strong
                                                >
                                            </td>
                                            <td
                                                v-if="
                                                    getSetupFeeStructure() ==
                                                        '1' ||
                                                    getSetupFeeStructure() ===
                                                        '1'
                                                "
                                            >
                                                {{ location.colorCount }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    getSetupFeeStructure() ==
                                                        '1' ||
                                                    getSetupFeeStructure() ===
                                                        '1'
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        isAdditionalLocationSetupSuppressedByDesignGroup(
                                                            location
                                                        )
                                                            ? 0
                                                            : getSetupFeeMasterData(
                                                                  "setup_fee_color1"
                                                              )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                <span
                                                    v-if="
                                                        location.isFullColor ===
                                                            true ||
                                                        location.colorCount > 1
                                                    "
                                                >
                                                    {{
                                                        formatPrice(
                                                            isAdditionalLocationSetupSuppressedByDesignGroup(
                                                                location
                                                            )
                                                                ? 0
                                                                : getSetupFeeMasterData(
                                                                      "setup_fee_multicolor"
                                                                  )
                                                        )
                                                    }}
                                                </span>
                                                <span v-else>
                                                    {{
                                                        formatPrice(
                                                            isAdditionalLocationSetupSuppressedByDesignGroup(
                                                                location
                                                            )
                                                                ? 0
                                                                : getSetupFeeMasterData(
                                                                      "setup_fee_color1"
                                                                  )
                                                        )
                                                    }}
                                                </span>
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        isAdditionalLocationSetupSuppressedByDesignGroup(
                                                            location
                                                        )
                                                            ? 0
                                                            : getAdditionalLocationSetupFee(
                                                                  location
                                                              )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 2 for Setup Fee) -->
                                    <template
                                        v-for="rollupGroup in getServicesGroupedByRollupFee(
                                            2
                                        )"
                                    >
                                        <!-- Services included in Setup Fee (show_service = false) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === false;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                            class="disable_data"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>

                                        <!-- Services shown as separate lines (show_service = true) -->
                                        <tr
                                            v-for="service in rollupGroup.services.filter(
                                                (s) => {
                                                    const show =
                                                        s.show_service !==
                                                        undefined
                                                            ? s.show_service
                                                            : true;
                                                    return show === true;
                                                }
                                            )"
                                            :key="service.id || service.name"
                                        >
                                            <td>
                                                {{
                                                    getServiceName(service) ||
                                                    service.name ||
                                                    service.service_name
                                                }}
                                            </td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{ quantity }}
                                            </td>
                                            <td v-else>1</td>
                                            <td
                                                v-if="
                                                    service.fee_structure ===
                                                        'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service
                                                            .fee_structure_name ===
                                                            'Per Item')
                                                "
                                            >
                                                {{
                                                    formatPrice(
                                                        getServiceUnitPrice(
                                                            service
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td v-else>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                {{
                                                    formatPrice(
                                                        getServicePrice(
                                                            service,
                                                            quantity
                                                        )
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Total Setup Fee -->
                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedSetupFeeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <span class="cost-label" v-else>
                    {{ getRollupFeeDisplayName(2) || "Setup Fee" }}
                </span>
                <span class="cost-value">{{
                    formatPrice(calculatedSetupFeeTotal)
                }}</span>
            </div>
            <!-- Rush Fee Row - Only visible when rush option is selected -->
            <div class="cost-row" v-if="selectedRushOptionId">
                <div class="tooltip-wrapper" @click.stop="toggleRushFeeTooltip">
                    <span class="cost-label"
                        >{{ getRollupFeeDisplayName(6) || "Rush Fee" }}
                    </span>
                    <svg
                        class="info-icon-svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 5.33203V8.66536"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M8 10.668H8.00599"
                            stroke="#929292"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>

                    <div
                        class="tooltip-content"
                        :class="{ 'is-visible': rushFeeTooltipVisible }"
                    >
                        <div class="tooltip-header">
                            <h4>
                                {{ getRollupFeeDisplayName(6) || "Rush Fee" }}
                                Breakdown
                            </h4>
                            <svg
                                class="close-icon"
                                @click.stop="closeRushFeeTooltip"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Service</th>
                                        <th>Qty</th>
                                        <th>Amt</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Selected Rush Option (from radio button) -->
                                    <tr
                                        v-if="
                                            selectedRushOptionId &&
                                            selectedRushOptionPrice > 0
                                        "
                                    >
                                        <td>
                                            <strong>{{
                                                getRushServiceNameWithDate(
                                                    selectedRushOptionLabel
                                                )
                                            }}</strong>
                                        </td>
                                        <td>
                                            {{
                                                selectedRushOptionId ===
                                                "rush_package"
                                                    ? 1
                                                    : quantity
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    selectedRushOptionPrice
                                                )
                                            }}
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    selectedRushOptionId ===
                                                        "rush_package"
                                                        ? selectedRushOptionPrice *
                                                              1
                                                        : selectedRushOptionPrice *
                                                              quantity
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>

                                    <!-- Services grouped by Rollup Fee (rollup_fee_id = 6 for Rush Fee) -->
                                    <template
                                        v-for="rollupGroup in rushFeeServices"
                                    >
                                        <tr
                                            v-for="service in rollupGroup.services"
                                            :key="
                                                'rush-fee-' +
                                                rollupGroup.rollup_fee_id +
                                                '-' +
                                                service.id
                                            "
                                            :class="{
                                                disable_data:
                                                    !getVendorServiceForRush(
                                                        service
                                                    ) ||
                                                    parseFloat(
                                                        service.retail_cost || 0
                                                    ) === 0
                                            }"
                                        >
                                            <td>
                                                {{
                                                    getRushServiceNameWithDate(
                                                        getServiceName(service)
                                                    )
                                                }}
                                            </td>
                                            <td>
                                                <span
                                                    v-if="
                                                        getServiceFeeStructureValue(
                                                            service
                                                        ) === 6
                                                    "
                                                >
                                                    {{ quantity }}
                                                </span>
                                                <span v-else>1</span>
                                            </td>
                                            <td>
                                                <span
                                                    v-if="
                                                        getVendorServiceForRush(
                                                            service
                                                        ) &&
                                                        parseFloat(
                                                            service.retail_cost ||
                                                                0
                                                        ) > 0
                                                    "
                                                >
                                                    {{
                                                        formatPrice(
                                                            parseFloat(
                                                                service.retail_cost ||
                                                                    0
                                                            )
                                                        )
                                                    }}
                                                </span>
                                                <span v-else class="text-muted"
                                                    >N/A</span
                                                >
                                            </td>
                                            <td>
                                                <span
                                                    v-if="
                                                        getVendorServiceForRush(
                                                            service
                                                        ) &&
                                                        parseFloat(
                                                            service.retail_cost ||
                                                                0
                                                        ) > 0
                                                    "
                                                >
                                                    {{
                                                        formatPrice(
                                                            getServiceFeeStructureValue(
                                                                service
                                                            ) === 6
                                                                ? parseFloat(
                                                                      service.retail_cost ||
                                                                          0
                                                                  ) * quantity
                                                                : parseFloat(
                                                                      service.retail_cost ||
                                                                          0
                                                                  )
                                                        )
                                                    }}
                                                </span>
                                                <span v-else class="text-muted"
                                                    >$0.00</span
                                                >
                                            </td>
                                        </tr>
                                    </template>

                                    <!-- Total Rush Fee -->
                                    <tr class="total-row">
                                        <td colspan="3">
                                            <strong>Total</strong>
                                        </td>
                                        <td>
                                            <strong>{{
                                                formatPrice(
                                                    calculatedRushFeeTotal
                                                )
                                            }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <span class="cost-value">{{
                    formatPrice(calculatedRushFeeTotal)
                }}</span>
            </div>
            <div class="cost-row" v-if="designFee > 0">
                <span class="cost-label">Design Fee</span>
                <span class="cost-value">{{ formatPrice(designFee) }}</span>
            </div>
            <div
                class="cost-row"
                v-if="(parseFloat(patchCost) || 0) > 0 || ((patchCostDetails || []).some(r => parseInt(r.inventory_used || 0, 10) > 0))"
            >
                <div
                    class="tooltip-wrapper"
                    @click.stop="togglePatchTooltip"
                    v-if="hasPatchTooltipData"
                >
                    <span class="cost-label">Patch</span>
                    <svg class="info-icon-svg" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.0026 14.6654C11.6693 14.6654 14.6693 11.6654 14.6693 7.9987C14.6693 4.33203 11.6693 1.33203 8.0026 1.33203C4.33594 1.33203 1.33594 4.33203 1.33594 7.9987C1.33594 11.6654 4.33594 14.6654 8.0026 14.6654Z" stroke="#929292" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 5.33203V8.66536" stroke="#929292" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 10.668H8.00599" stroke="#929292" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div class="tooltip-content" :class="{ 'is-visible': patchTooltipVisible }">
                        <div class="tooltip-header">
                            <h4>Patch Breakdown</h4>
                            <svg class="close-icon" @click.stop="closePatchTooltip" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="tooltip-body">
                            <table class="base-fee-table">
                                <thead>
                                    <tr>
                                        <th>Patch</th>
                                        <th>QTY</th>
                                        <th>Amount</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(row, idx) in (patchCostDetails || [])">
                                        <!-- Base Patch Row (only when slab charging happens) -->
                                        <tr
                                            v-if="parseInt(row.charged_quantity || row.total || 0, 10) > 0"
                                            :key="'patch-base-' + idx"
                                        >
                                            <td>
                                                {{ row.patchName }}{{ row.materialName ? ' (' + row.materialName + ')' : '' }}{{ (row.width && row.height) ? ' - ' + row.width + '" x ' + row.height + '"' : '' }}
                                            </td>
                                            <td>{{ ((parseInt(row.charged_quantity || 0, 10) || 0) + (parseInt(row.inventory_used || 0, 10) || 0)) || row.quantity || row.slab }}</td>
                                            <td>{{ formatPrice(row.perPatchPrice) }}</td>
                                            <td>{{ formatPrice(((((parseInt(row.charged_quantity || 0, 10) || 0) + (parseInt(row.inventory_used || 0, 10) || 0)) || (parseInt(row.quantity || row.slab || 0, 10) || 0)) * (row.perPatchPrice || 0))) }}</td>
                                        </tr>
                                        <!-- Inventory Row (only when inventory_used > 0) -->
                                        <tr
                                            v-if="row.inventory_used && row.inventory_used > 0"
                                            :key="'patch-inv-' + idx"
                                        >
                                            <td>Inventory</td>
                                            <td>
                                                <span v-if="parseInt(row.charged_quantity || row.total || 0, 10) > 0">
                                                    -{{ row.inventory_used }}
                                                </span>
                                                <span v-else>
                                                    {{ row.inventory_used }}
                                                </span>
                                            </td>
                                            <td>{{ formatPrice(row.perPatchPrice) }}</td>
                                            <td>
                                                {{
                                                    parseInt(row.charged_quantity || row.total || 0, 10) > 0
                                                        ? formatPrice(-1 * (row.inventory_used || 0) * (row.perPatchPrice || 0))
                                                        : formatPrice((row.inventory_used || 0) * (row.perPatchPrice || 0))
                                                }}
                                            </td>
                                        </tr>
                                    </template>
                                    <tr
                                        v-for="service in patchAdditionalServicesFlat"
                                        :key="'patch-svc-' + service.id"
                                    >
                                        <td>{{ getServiceName(service) }}</td>
                                        <td>
                                            {{
                                                (service.fee_structure === 'Per Item' ||
                                                    (service.rollup_service &&
                                                        service.rollup_service.fee_structure_name === 'Per Item'))
                                                    ? quantity
                                                    : 1
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                formatPrice(
                                                    (service.fee_structure === 'Per Item' ||
                                                        (service.rollup_service &&
                                                            service.rollup_service.fee_structure_name === 'Per Item'))
                                                        ? getServiceUnitPrice(service)
                                                        : getServicePrice(service, quantity)
                                                )
                                            }}
                                        </td>
                                        <td>{{ formatPrice(getServicePrice(service, quantity)) }}</td>
                                    </tr>
                                    <tr class="total-row" v-if="hasPatchTooltipData">
                                        <td colspan="3"><strong>Total</strong></td>
                                        <td><strong>{{ formatPrice(displayPatchTotal) }}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <span class="cost-label" v-else>Patch</span>
                <span class="cost-value">{{ formatPrice(displayPatchTotal) }}</span>
            </div>
            <div
                v-if="(parseFloat(patchCost) || 0) > 0 && patchCostWarning"
                class="patch-cost-warning-msg small text-danger font-weight-bold mt-1 mb-2"
            >
                {{ patchCostWarning }}
            </div>

            <hr class="cost-separator" />
            <div class="cost-row total-row">
                <h5 class="cost-label-total">Total Cost</h5>
                <h5 class="cost-value-total">
                    {{ formatPrice(preDiscountTotal) }}
                </h5>
            </div>
        </div>
    </div>
</template>

<script>
import { rollupFeeMixin } from "@/common/rollupFee.service";
import { ecommDisplayNameMixin } from "@/common/ecommDisplayName.service";
import { computeFingerprintFromArbitraryPayload } from "@/common/suppressDesignGroupFingerprint";
import {
    getLocationGroupingMetadataByColorCountOnly,
    isImprintLocationSuppressedByColorCountOnly,
    normalizeImprintLocationKey
} from "@/common/itemSuppressDisplayShared";

export default {
    name: "ImprintCityPriceBreakdown",
    mixins: [rollupFeeMixin, ecommDisplayNameMixin],
    data() {
        return {
            tooltipVisible: false,
            runChargeTooltipVisible: false,
            setupFeeTooltipVisible: false,
            colorMixFeeTooltipVisible: false,
            rushFeeTooltipVisible: false,
            patchTooltipVisible: false
        };
    },
    props: {
        quantity: { type: Number, default: 0 },
        designFee: { type: Number, default: 0 },
        runChargeDetails: { type: Array, default: () => [] },
        otherServices: { type: Array, default: () => [] },
        totalDiscount: { type: Number, default: 0 },
        discountConfig: { type: Object, default: () => null },
        baseCost: { type: Number, default: 0 },
        runCharge: { type: Number, default: 0 },
        setupFee: { type: Number, default: 0 },
        totalCost: { type: Number, default: 0 },
        imprintLocations: { type: Array, default: () => [] },
        vendorColors: { type: Array, default: () => [] },
        serviceMasterData: { type: Object, default: () => ({}) },
        activeDecorationService: { type: Object, default: () => null },
        isDesignStudio: { type: Boolean, default: false },
        decorationPricingData: { type: [Array, Object], default: () => null },
        decorationType: { type: Number, default: 1 },
        totalQuantity: { type: Number, default: 0 },
        stitchCount: { type: Number, default: 0 },
        colorSizeCombinations: { type: Array, default: () => [] },
        selectedRushOptionId: { type: String, default: null },
        selectedRushOptionDate: { type: String, default: null },
        vendorServices: { type: Array, default: () => [] },
        activeServiceId: { type: Number, default: null },
        dynamicRushOptions: { type: Array, default: () => [] },
        patchCost: { type: Number, default: 0 },
        patchCostTooltip: { type: String, default: "" },
        patchSlabQuantity: { type: Number, default: 0 },
        patchCostDetails: { type: Array, default: () => [] },
        patchCostWarning: { type: String, default: "" },
        /** When false, ignore same-design-group suppression and show full catalog run/setup math. */
        applyDesignGroupSuppress: { type: Boolean, default: true },
        /** Product details: group by color count only (1+1, 2+2, full_color+full_color). */
        suppressGroupByColorCountOnly: { type: Boolean, default: false }
    },
    computed: {
        designGroupSuppressEffective() {
            if (this.applyDesignGroupSuppress === false) {
                return false;
            }
            if (!this.readSuppressAdditionalSetupRunChargeActive()) {
                return false;
            }
            if (this.suppressGroupByColorCountOnly) {
                const selected = (this.imprintLocations || []).filter(
                    (l) => l && l.is_selected
                );
                return selected.length >= 2;
            }
            return true;
        },
        /** Keep run charge row + tooltip visible when suppress zeros the total but imprints exist (Design Studio / cart). */
        showRunChargeCostRow() {
            if (this.calculatedRunChargeTotal > 0) {
                return true;
            }
            if (!this.designGroupSuppressEffective) {
                return false;
            }
            return this.hasRunChargeBreakdownDecorationContext();
        },
        otherServicesTotal() {
            // Only include services with rollup_fee_id = 1 (Base Fee)
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let total = 0;

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    total += this.getServicePrice(service, this.quantity);
                });
            });

            return total;
        },
        displayBaseCost() {
            // If color-size combinations exist, calculate from them (already discounted)
            if (
                this.colorSizeCombinations &&
                this.colorSizeCombinations.length > 0
            ) {
                let total = 0;

                // Sum up color-size combinations (already discounted in getColorSizeUnitPrice)
                this.colorSizeCombinations.forEach((combination) => {
                    total += this.getColorSizeTotalPrice(combination);
                });

                // Add services with show_service = true (not discounted)
                const baseFeeServices = this.getServicesGroupedByRollupFee(1);
                baseFeeServices.forEach((rollupGroup) => {
                    rollupGroup.services.forEach((service) => {
                        const show =
                            service.show_service !== undefined
                                ? service.show_service
                                : true;
                        if (show === true) {
                            // Services with show_service = true are not discounted
                            total += this.getServicePrice(
                                service,
                                this.quantity
                            );
                        }
                    });
                });

                return parseFloat(total.toFixed(2));
            }

            // Fallback: Only include services with rollup_fee_id = 1 (Base Fee)
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let includedServicesTotal = 0; // Services with show_service = false
            let otherServicesTotal = 0; // Services with show_service = true

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === false) {
                        // Included services: unit price × quantity
                        const serviceUnitPrice =
                            this.getServiceUnitPrice(service);
                        includedServicesTotal +=
                            serviceUnitPrice * this.quantity;
                    } else {
                        // Other services: use getServicePrice which handles Per Item vs Per Order
                        otherServicesTotal += this.getServicePrice(
                            service,
                            this.quantity
                        );
                    }
                });
            });

            const baseTotal = parseFloat(
                (
                    this.baseCost +
                    includedServicesTotal +
                    otherServicesTotal
                ).toFixed(2)
            );

            // Apply discount if activeProductDiscount exists
            return this.applyDiscountToPrice(baseTotal);
        },
        // Calculate total from individual rows in tooltip (Base Fee only; patch has its own row)
        getTooltipTotal() {
            let total = 0;

            // Sum up color-size combinations (already discounted in getColorSizeUnitPrice)
            if (
                this.colorSizeCombinations &&
                this.colorSizeCombinations.length > 0
            ) {
                this.colorSizeCombinations.forEach((combination) => {
                    total += this.getColorSizeTotalPrice(combination);
                });
            } else {
                // Fallback: if no color-size combinations, use displayBaseCost (patch is shown in its own row)
                return parseFloat((this.displayBaseCost).toFixed(2));
            }

            // Sum up services with show_service = true (not discounted)
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === true) {
                        // Services with show_service = true are not discounted
                        total += this.getServicePrice(service, this.quantity);
                    }
                });
            });

            return parseFloat(total.toFixed(2));
        },
        togglePatchTooltip() {
            this.patchTooltipVisible = !this.patchTooltipVisible;
            this.tooltipVisible = false;
            this.runChargeTooltipVisible = false;
            this.setupFeeTooltipVisible = false;
            this.colorMixFeeTooltipVisible = false;
            this.rushFeeTooltipVisible = false;
        },
        closePatchTooltip() {
            this.patchTooltipVisible = false;
        },
        // Get Rush Fee services (rollup_fee_id = 6)
        rushFeeServices() {
            return this.getServicesGroupedByRollupFee(6);
        },
        // Get selected rush option label
        selectedRushOptionLabel() {
            if (!this.selectedRushOptionId) return null;
            const rushOptions = {
                rush1: "Rush 1 Day",
                rush3: "Rush 3 Day",
                rush5: "Rush 5 Day",
                rush7: "Rush 7 Day",
                rush_package: "Rush Package"
            };
            return rushOptions[this.selectedRushOptionId] || null;
        },
        // Get selected rush option price from vendor services
        selectedRushOptionPrice() {
            if (
                !this.selectedRushOptionId ||
                !this.activeServiceId ||
                !this.vendorServices
            ) {
                return 0;
            }

            // Find vendor service that matches the active service ID
            const vendorService = this.vendorServices.find(
                (vs) => vs.id === this.activeServiceId
            );
            if (!vendorService) {
                return 0;
            }

            // Get price based on selected rush option
            // Note: Selected rush service prices are NOT multiplied by quantity (fixed price)
            let price = 0;
            switch (this.selectedRushOptionId) {
                case "rush1":
                    price = parseFloat(vendorService.rush1 || 0);
                    return price;
                case "rush3":
                    price = parseFloat(vendorService.rush3 || 0);
                    return price;
                case "rush5":
                    price = parseFloat(vendorService.rush5 || 0);
                    return price;
                case "rush7":
                    price = parseFloat(vendorService.rush7 || 0);
                    return price;
                case "rush_package":
                    price = parseFloat(vendorService.rush_package || 0);
                    return price;
                default:
                    return 0;
            }
        },
        // Calculate Rush Fee total
        calculatedRushFeeTotal() {
            if (!this.selectedRushOptionId) {
                return 0;
            }

            let rushFeeTotal = 0;

            // Add selected rush option price
            // If rush_package, multiply by 1 (use price as-is)
            // For other rush options (rush1, rush3, rush5, rush7), multiply by quantity
            if (this.selectedRushOptionId === "rush_package") {
                rushFeeTotal += this.selectedRushOptionPrice * 1;
            } else {
                rushFeeTotal += this.selectedRushOptionPrice * this.quantity;
            }

            // Add rollup fee services (rollup_fee_id = 6)
            const rushFeeServices = this.rushFeeServices;

            // Process each rollup group
            rushFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    // Check if vendor service is available (has retail_cost > 0)
                    const vendorService = this.getVendorServiceForRush(service);
                    const servicePrice = parseFloat(service.retail_cost || 0);

                    // Only add to total if service has a price (vendor service is available)
                    if (vendorService && servicePrice > 0) {
                        // Check fee_structure - only multiply by quantity if fee_structure = 6 (Per Item)
                        const feeStructure =
                            this.getServiceFeeStructureValue(service);
                        if (feeStructure === 6) {
                            // Per Item: multiply by quantity
                            rushFeeTotal += servicePrice * this.quantity;
                        } else {
                            // Per Order: use price as-is
                            rushFeeTotal += servicePrice;
                        }
                    }
                    // If vendor service is not available (price is 0 or null), don't add to total but still show in tooltip
                });
            });

            return parseFloat(rushFeeTotal.toFixed(2));
        },
        // Calculate pre-discount total (subtotal before discount)
        // Patch rollup_fee_id = 9 (Patch service_id = 9); same pattern as Run Charge (12)
        patchAdditionalServicesGroups() {
            return this.getServicesGroupedByRollupFee(9);
        },
        patchAdditionalServicesFlat() {
            const groups = this.patchAdditionalServicesGroups;
            if (!groups || groups.length === 0) return [];
            return groups.reduce((acc, g) => acc.concat(g.services || []), []);
        },
        calculatedPatchAdditionalTotal() {
            let total = 0;
            this.patchAdditionalServicesGroups.forEach((rollupGroup) => {
                (rollupGroup.services || []).forEach((service) => {
                    total += this.getServicePrice(service, this.quantity);
                });
            });
            return parseFloat(total.toFixed(2));
        },
        displayPatchTotal() {
            const base = parseFloat(this.patchCost) || 0;
            const additional = this.calculatedPatchAdditionalTotal || 0;
            return parseFloat((base + additional).toFixed(2));
        },
        hasPatchTooltipData() {
            return (
                (this.patchCostDetails && this.patchCostDetails.length > 0) ||
                this.patchAdditionalServicesFlat.length > 0
            );
        },
        preDiscountTotal() {
            const base = this.displayBaseCost || 0;
            const runCharge = this.calculatedRunChargeTotal || 0;
            const colorMixFee = this.calculatedColorMixFeeTotal || 0;
            const setupFee = this.calculatedSetupFeeTotal || 0;
            const designFee = this.designFee || 0;
            const rushFee = this.calculatedRushFeeTotal || 0;
            const patchCostVal = parseFloat(this.patchCost) || 0;
            const patchAdditionalVal =
                patchCostVal > 0 ? this.calculatedPatchAdditionalTotal || 0 : 0;
            return parseFloat(
                (
                    base +
                    runCharge +
                    colorMixFee +
                    setupFee +
                    designFee +
                    rushFee +
                    patchCostVal +
                    patchAdditionalVal
                ).toFixed(2)
            );
        },
        // Discount calculation removed - no longer applying overall discount
        subtotal() {
            // Subtotal is the pre-discount total
            return this.preDiscountTotal;
        },
        // Calculate total run charge from all breakdown components
        calculatedRunChargeTotal() {
            const suppress = this.designGroupSuppressEffective;
            const selectedOrdered = (this.imprintLocations || []).filter(
                (l) => l && l.is_selected
            );
            if (suppress && selectedOrdered.length > 0) {
                return this.computeCalculatedRunChargeTotalSuppress();
            }

            let total = 0;
            const decorationType = parseInt(this.decorationType) || 0;

            // Add first location full color value if full color is selected
            const firstLocation = this.getFirstLocationData();
            if (firstLocation && this.isFirstLocationFullColor()) {
                const fullColorValue = this.getFirstLocationFullColorValue();
                if (fullColorValue > 0) {
                    total += fullColorValue * this.quantity;
                }
            }

            // Handle decoration_type 1 (Print) and decoration_type 2 (Embroidery) differently
            if (decorationType === 1 || decorationType === 5) {
                // PRINT LOGIC
                if (firstLocation) {
                    const firstLocationMasterCost =
                        this.getServiceMasterData("location_1st") || 0;
                    if (firstLocationMasterCost > 0) {
                        total += firstLocationMasterCost * this.quantity;
                    }
                    if (!this.isFirstLocationFullColor()) {
                        const firstColorMasterCost =
                            this.getServiceMasterData("cost_per_color_1st") || 0;
                        if (firstColorMasterCost > 0) {
                            total += firstColorMasterCost * this.quantity;
                        }
                    }
                }
                // Add first location additional colors (if > 1 and not full color)
                if (!this.isFirstLocationFullColor()) {
                    const firstLocationAddlColors =
                        this.getFirstLocationAdditionalColors();
                    if (firstLocationAddlColors > 0) {
                        const firstLocationAddlPrice =
                            this.getFirstLocationAddlColorPrice();
                        if (firstLocationAddlPrice > 0) {
                            total +=
                                firstLocationAddlPrice *
                                this.getLocation1AdditionalColorUnits();
                        }
                    }
                }

                // Add additional locations run charge
                const additionalLocations = this.getAdditionalLocations();
                additionalLocations.forEach((location) => {
                    const additionalLocationBase =
                        this.getServiceMasterData("additional_location") || 0;
                    if (additionalLocationBase > 0) {
                        total += additionalLocationBase * this.quantity;
                    }

                    // Check if full color is selected for this location
                    if (location.isFullColor === true) {
                        // For full color, use same value as first location
                        const fullColorValue =
                            this.getFirstLocationFullColorValue();
                        if (fullColorValue > 0) {
                            total += fullColorValue * this.quantity;
                        }
                    } else if (location.colorCount > 0) {
                        const loc2ColorCharge =
                            this.getType1SecondLocationAdditionalColorTotal(
                                location
                            );
                        if (loc2ColorCharge > 0) {
                            total += loc2ColorCharge;
                        }
                    }
                });
            } else if (
                decorationType === 2 ||
                decorationType === 4
            ) {
                // EMBROIDERY LOGIC - matches UseImprintCalculator.calculateRunCharge()
                if (firstLocation) {
                    const firstLocationMasterCost =
                        this.getServiceMasterData("location_1st") || 0;
                    if (firstLocationMasterCost > 0) {
                        total += firstLocationMasterCost * this.quantity;
                    }
                    if (!this.isFirstLocationFullColor()) {
                        const firstColorMasterCost =
                            this.getServiceMasterData("cost_per_color_1st") || 0;
                        if (firstColorMasterCost > 0) {
                            total += firstColorMasterCost * this.quantity;
                        }

                        const firstLocationAddlColors =
                            this.getFirstLocationAdditionalColors();
                        const firstLocationAddlPrice =
                            this.getFirstLocationAddlColorPrice();
                        if (
                            firstLocationAddlColors > 0 &&
                            firstLocationAddlPrice > 0
                        ) {
                            total +=
                                firstLocationAddlPrice *
                                this.getLocation1AdditionalColorUnits();
                        }
                    }
                }

                const additionalLocations = this.getAdditionalLocations();
                additionalLocations.forEach((location) => {
                    const additionalLocationBase =
                        this.getServiceMasterData("additional_location") || 0;
                    if (additionalLocationBase > 0) {
                        total += additionalLocationBase * this.quantity;
                    }

                    // Check if full color is selected for this location
                    if (location.isFullColor === true) {
                        // For full color, use same value as first location
                        const fullColorValue =
                            this.getFirstLocationFullColorValue();
                        if (fullColorValue > 0) {
                            total += fullColorValue * this.quantity;
                        }
                    } else if (location.colorCount > 0) {
                        // For embroidery, use location2 price from decoration bulk data
                        const embroideryPrice =
                            this.getEmbroideryLocationPrice(location);
                        if (embroideryPrice > 0) {
                            total += embroideryPrice * this.quantity;
                        }

                        const addlEmbroideryColorTotal =
                            this.getEmbroideryLocation2AdditionalColorTotal(
                                location
                            );
                        if (addlEmbroideryColorTotal > 0) {
                            total += addlEmbroideryColorTotal;
                        }
                    }
                });
            }

            // Add specialty color upcharge for first location
            const firstLocationColorUpcharge =
                this.getFirstLocationColorUpcharge();
            if (firstLocationColorUpcharge > 0) {
                total += firstLocationColorUpcharge * this.quantity;
            }

            // Add custom color upcharge for first location
            const firstLocationCustomColorUpcharge =
                this.getFirstLocationCustomColorUpcharge();
            if (firstLocationCustomColorUpcharge > 0) {
                total += firstLocationCustomColorUpcharge * this.quantity;
            }

            // Add name/number fees for first location
            const firstLocationNameNumberFees =
                this.getFirstLocationNameNumberFees();
            firstLocationNameNumberFees.forEach((fee) => {
                total += fee.total;
            });

            // Add additional locations color upcharge and name/number fees
            const additionalLocations = this.getAdditionalLocations();
            additionalLocations.forEach((location) => {
                // Specialty color upcharge for this location
                if (location.location) {
                    const locationColorUpcharge = this.getLocationColorUpcharge(
                        location.location
                    );
                    if (locationColorUpcharge > 0) {
                        total += locationColorUpcharge * this.quantity;
                    }

                    // Custom color upcharge for this location
                    const locationCustomColorUpcharge =
                        this.getLocationCustomColorUpcharge(location.location);
                    if (locationCustomColorUpcharge > 0) {
                        total += locationCustomColorUpcharge * this.quantity;
                    }
                }

                // Name/number fees
                const nameNumberFees = this.getLocationNameNumberFees(
                    location.location
                );
                nameNumberFees.forEach((fee) => {
                    total += fee.total;
                });
            });

            // Add stitch cost for embroidery services
            if (this.isEmbroideryService() && this.stitchCount > 0) {
                const stitchCost = this.getStitchCost();
                if (stitchCost > 0) {
                    total += stitchCost * this.quantity;
                }
            }

            // Add other run charge details (excluding location, color mix, name/number fees)
            const otherRunChargeDetails = this.getOtherRunChargeDetails();
            otherRunChargeDetails.forEach((detail) => {
                total += parseFloat(detail.value || 0);
            });

            // Add services with rollup_fee_id = 12 (Run Charge services) - include all services regardless of show_service
            const runChargeServices = this.getServicesGroupedByRollupFee(12);
            runChargeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    total += this.getServicePrice(service, this.quantity);
                });
            });

            return parseFloat(total.toFixed(2));
        },
        // Calculate Custom Color Fee total
        calculatedColorMixFeeTotal() {
            let total = 0;

            const mixFeeData = this.getOrderMixFeeData();
            if (mixFeeData && mixFeeData.total > 0) {
                total += mixFeeData.total;
            }

            // Add services with rollup_fee_id = 13 (Custom Color Fee services) - include all services regardless of show_service
            const colorMixFeeServices = this.getServicesGroupedByRollupFee(13);
            colorMixFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    total += this.getServicePrice(service, this.quantity);
                });
            });

            return parseFloat(total.toFixed(2));
        },
        // Calculate total setup fee from all locations (suppress matches PCNA)
        calculatedSetupFeeTotal() {
            return this.computeCalculatedSetupFeeTotal();
        },
        // Get additional location rows for run charge
        additionalLocationRows() {
            const locations = this.getAdditionalLocations();
            const rows = [];
            const decorationType = parseInt(this.decorationType) || 0;
            const suppressAdditionalEnabled =
                this.designGroupSuppressEffective;
            const selectedAll = (this.imprintLocations || []).filter(
                (l) => l && l.is_selected
            );
            const groupedAll = suppressAdditionalEnabled
                ? this.getPcnaLocationGroupingMetadata(
                      selectedAll,
                      this.designGroupSuppressEffective
                  )
                : null;
            const groupingByImprintRef = new Map();
            if (groupedAll) {
                groupedAll.forEach((g) => {
                    groupingByImprintRef.set(g.location, g);
                    const locKey = normalizeImprintLocationKey(
                        g.location && g.location.location
                    );
                    if (locKey) {
                        groupingByImprintRef.set(locKey, g);
                    }
                });
            }
            locations.forEach((location, idx) => {
                const imprintLoc = location.location || {};
                let isGroupPrimary = true;
                let isSuppressed = false;
                if (suppressAdditionalEnabled && groupedAll) {
                    if (this.suppressGroupByColorCountOnly) {
                        isSuppressed =
                            isImprintLocationSuppressedByColorCountOnly(
                                selectedAll,
                                imprintLoc
                            );
                        isGroupPrimary = !isSuppressed;
                    } else {
                        const meta =
                            groupingByImprintRef.get(imprintLoc) ||
                            groupingByImprintRef.get(
                                normalizeImprintLocationKey(
                                    imprintLoc.location
                                )
                            );
                        isGroupPrimary = meta ? !!meta.isGroupPrimary : true;
                        isSuppressed =
                            suppressAdditionalEnabled && !isGroupPrimary;
                    }
                }
                const additionalLocationBase =
                    this.getServiceMasterData("additional_location") || 0;
                const isEmbroideryDecoration =
                    decorationType === 2 || decorationType === 4;
                const hasLocationColors =
                    location &&
                    location.isFullColor !== true &&
                    (parseInt(location.colorCount) || 0) > 0;
                const embroideryLocationUnit = isEmbroideryDecoration
                    ? this.getEmbroideryLocationPrice(location)
                    : 0;
                const additionalLocationUnit =
                    isEmbroideryDecoration && hasLocationColors
                        ? additionalLocationBase + embroideryLocationUnit
                        : additionalLocationBase;

                if (isSuppressed) {
                    rows.push({
                        type: "location",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        qty: this.quantity,
                        amt: 0,
                        total: 0,
                        isIncluded: false,
                        is_suppressed_by_group: true
                    });
                    if (location.isFullColor === true || location.colorCount > 0) {
                        rows.push({
                            type: location.isFullColor === true ? "fullColor" : "addlColor",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: 0,
                            total: 0,
                            isIncluded: false,
                            is_suppressed_by_group: true,
                            colorCount: location.colorCount
                        });
                    }
                    const firstSpec = this.getFirstLocationColorUpcharge();
                    if (firstSpec > 0) {
                        rows.push({
                            type: "colorUpcharge",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: 0,
                            total: 0,
                            isIncluded: false,
                            is_suppressed_by_group: true
                        });
                    }
                    const firstCust = this.getFirstLocationCustomColorUpcharge();
                    if (firstCust > 0) {
                        rows.push({
                            type: "customColorUpcharge",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: 0,
                            total: 0,
                            isIncluded: false,
                            is_suppressed_by_group: true
                        });
                    }
                    return;
                }

                // Additional Location base row
                if (additionalLocationUnit > 0) {
                    rows.push({
                        type: "location",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        qty: this.quantity,
                        amt: additionalLocationUnit,
                        total: additionalLocationUnit * this.quantity,
                        isIncluded: false
                    });
                } else {
                    rows.push({
                        type: "location",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        qty: this.quantity,
                        amt: "INCL",
                        total: "INCL",
                        isIncluded: true
                    });
                }

                // Full Color or Addl Color for additional locations
                if (location.isFullColor === true) {
                    // Full color for additional location - use same value as first location
                    const fullColorValue =
                        this.getFirstLocationFullColorValue();

                    if (fullColorValue > 0) {
                        rows.push({
                            type: "fullColor",
                            locationIndex: idx + 1,
                            locationName: location.locationName,
                            qty: this.quantity,
                            amt: fullColorValue,
                            total: fullColorValue * this.quantity,
                            isIncluded: false,
                            isFullColor: true
                        });
                    }
                } else if (location.colorCount > 0) {
                    if (decorationType === 1 || decorationType === 5) {
                        const combinedUnit =
                            this.getType1SecondLocationAdditionalColorUnitPrice(
                                location.colorCount
                            );
                        if (
                            location.colorCount > 0 &&
                            combinedUnit > 0
                        ) {
                            rows.push({
                                type: "addlColor",
                                locationIndex: idx + 1,
                                locationName: location.locationName,
                                qty:
                                    this.getLocation2AdditionalColorUnits(
                                        location.colorCount
                                    ),
                                amt: combinedUnit,
                                total:
                                    combinedUnit *
                                    this.getLocation2AdditionalColorUnits(
                                        location.colorCount
                                    ),
                                isIncluded: false,
                                colorCount: location.colorCount
                            });
                        }
                    } else if (
                        decorationType === 2 ||
                        decorationType === 4
                    ) {
                        const addlColorUnit =
                            this.getServiceMasterData(
                                "cost_per_color_2ndormore"
                            ) || 0;
                        if (location.colorCount > 0 && addlColorUnit > 0) {
                            const addlCount = location.colorCount;
                            rows.push({
                                type: "addlColor",
                                locationIndex: idx + 1,
                                locationName: location.locationName,
                                qty: addlCount * this.quantity,
                                amt: addlColorUnit,
                                total:
                                    addlColorUnit *
                                    addlCount *
                                    this.quantity,
                                isIncluded: false,
                                colorCount: addlCount
                            });
                        }
                    }
                }

                // Specialty Color Upcharge for this location
                const locationColorUpcharge = this.getLocationColorUpcharge(
                    location.location
                );
                if (locationColorUpcharge > 0) {
                    rows.push({
                        type: "colorUpcharge",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        qty: this.quantity,
                        amt: locationColorUpcharge,
                        total: locationColorUpcharge * this.quantity,
                        isIncluded: false
                    });
                }

                // Custom Color Upcharge for this location
                const locationCustomColorUpcharge =
                    this.getLocationCustomColorUpcharge(location.location);
                if (locationCustomColorUpcharge > 0) {
                    rows.push({
                        type: "customColorUpcharge",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        qty: this.quantity,
                        amt: locationCustomColorUpcharge,
                        total: locationCustomColorUpcharge * this.quantity,
                        isIncluded: false
                    });
                }

                // Name/Number Fees for this additional location
                const nameNumberFees = this.getLocationNameNumberFees(
                    location.location
                );
                nameNumberFees.forEach((fee) => {
                    rows.push({
                        type: "nameNumberFee",
                        locationIndex: idx + 1,
                        locationName: location.locationName,
                        name: fee.name,
                        qty: fee.quantity || this.quantity,
                        amt: fee.perItemFee,
                        total: fee.total
                    });
                });
            });
            return rows;
        }
    },
    methods: {
        readSuppressAdditionalSetupRunChargeActive() {
            const d = this.serviceMasterData || {};
            const raw = d.suppress_additional_setup_run_charge;
            return (
                Number(raw) === 1 || raw === true || raw === "1" || raw === 1
            );
        },
        getPcnaLocationColorSignature(location) {
            if (!location || !Array.isArray(location.colors)) return "";
            const signature = location.colors
                .map((entry) => {
                    const color = entry && entry.color ? entry.color : {};
                    return String(
                        color.color_id ||
                            color.id ||
                            color.hex ||
                            color.color_value ||
                            color.name ||
                            ""
                    ).toLowerCase();
                })
                .filter(Boolean)
                .sort();
            return signature.join("|");
        },
        getPcnaLocationDesignComparablePayload(location) {
            if (!location || typeof location !== "object") return {};
            const payload =
                location.design_data ||
                location.design_json ||
                location.artwork_data ||
                location.canvas_json ||
                location.objects ||
                location.print_data ||
                {};
            let normalizedPayload = payload;
            if (typeof normalizedPayload === "string") {
                try {
                    normalizedPayload = JSON.parse(normalizedPayload);
                } catch (e) {
                    normalizedPayload = { raw: normalizedPayload };
                }
            }
            const skipKeys = new Set([
                "left",
                "top",
                "x",
                "y",
                "angle",
                "rotate",
                "rotation",
                "scaleX",
                "scaleY",
                "translateX",
                "translateY",
                "posX",
                "posY"
            ]);
            const normalizeNode = (node) => {
                if (Array.isArray(node)) return node.map(normalizeNode);
                if (!node || typeof node !== "object") return node;
                return Object.keys(node)
                    .sort()
                    .reduce((acc, key) => {
                        if (skipKeys.has(key)) return acc;
                        acc[key] = normalizeNode(node[key]);
                        return acc;
                    }, {});
            };
            return normalizeNode(normalizedPayload);
        },
        getPcnaLocationComparisonKey(location) {
            const colorSignature = this.getPcnaLocationColorSignature(location);
            const designPayload =
                this.getPcnaLocationDesignComparablePayload(location);
            const fp =
                (location.suppress_design_fingerprint &&
                    String(location.suppress_design_fingerprint)) ||
                computeFingerprintFromArbitraryPayload(
                    location.design_data ||
                        location.design_json ||
                        location.artwork_data ||
                        location.canvas_json ||
                        location.objects ||
                        location.print_data
                );
            const designIdentity =
                location.design_id ||
                location.saved_design_id ||
                location.artwork_id ||
                location.image_url ||
                location.artwork_url ||
                "";
            const payloadSignature = JSON.stringify(designPayload || {});
            return [
                colorSignature,
                String(designIdentity),
                String(fp),
                payloadSignature
            ].join("::");
        },
        getPcnaLocationGroupingMetadata(imprintLocations, suppressionEnabled) {
            const selected = (imprintLocations || []).filter(
                (loc) => loc && loc.is_selected
            );
            if (
                suppressionEnabled &&
                this.suppressGroupByColorCountOnly
            ) {
                return getLocationGroupingMetadataByColorCountOnly(
                    imprintLocations
                );
            }
            const hasPresetGrouping = selected.some(
                (location) =>
                    location.design_group_id !== undefined &&
                    location.design_group_id !== null &&
                    location.design_group_id !== ""
            );
            if (suppressionEnabled && hasPresetGrouping) {
                const seenPreset = new Set();
                return selected.map((location, index) => {
                    const presetKey = String(
                        location.design_group_id || `preset_${index}`
                    );
                    const derivedPrimary =
                        location.is_group_primary !== undefined
                            ? !!location.is_group_primary
                            : !seenPreset.has(presetKey);
                    if (derivedPrimary) {
                        seenPreset.add(presetKey);
                    }
                    return {
                        location,
                        key: presetKey,
                        isGroupPrimary: derivedPrimary
                    };
                });
            }
            if (!suppressionEnabled) {
                return selected.map((location, index) => ({
                    location,
                    key: `legacy_${index}`,
                    isGroupPrimary: index === 0
                }));
            }
            const seenKeys = new Set();
            const logComputedKeys =
                suppressionEnabled &&
                !hasPresetGrouping &&
                this.designGroupSuppressEffective;
            return selected.map((location) => {
                const key = this.getPcnaLocationComparisonKey(location);
                const isGroupPrimary = !seenKeys.has(key);
                if (isGroupPrimary) {
                    seenKeys.add(key);
                }
                return {
                    location,
                    key,
                    isGroupPrimary
                };
            });
        },
        isImprintLocationFullColor(imprintLoc) {
            if (!imprintLoc) return false;
            if (
                imprintLoc.no_of_color &&
                imprintLoc.no_of_color !== "full_color"
            ) {
                const colorNum = parseInt(imprintLoc.no_of_color);
                if (!isNaN(colorNum)) {
                    return false;
                }
            }
            return (
                imprintLoc.is_full_color_selected === true ||
                imprintLoc.is_full_color === true ||
                imprintLoc.no_of_color === "full_color"
            );
        },
        buildAdditionalLocationWrapperFromImprint(imprintLoc) {
            if (!imprintLoc) {
                return {
                    index: 0,
                    locationName: "Location",
                    colorCount: 0,
                    isFullColor: false,
                    fullColorValue: null,
                    location: imprintLoc
                };
            }
            let isFullColor = false;
            if (
                imprintLoc.no_of_color &&
                imprintLoc.no_of_color !== "full_color"
            ) {
                const colorNum = parseInt(imprintLoc.no_of_color);
                if (!isNaN(colorNum)) {
                    isFullColor = false;
                } else {
                    isFullColor =
                        imprintLoc.is_full_color_selected === true ||
                        imprintLoc.is_full_color === true;
                }
            } else {
                isFullColor =
                    imprintLoc.is_full_color_selected === true ||
                    imprintLoc.is_full_color === true ||
                    imprintLoc.no_of_color === "full_color";
            }
            const colorCount = isFullColor
                ? 1
                : parseInt(imprintLoc.no_of_color) || 0;
            return {
                index: 0,
                locationName: imprintLoc.location || "Location",
                colorCount,
                isFullColor,
                fullColorValue: imprintLoc.full_color_value,
                location: imprintLoc
            };
        },
        getPrimaryStyleSetupFeeForImprint(imprintLoc) {
            if (!imprintLoc || !imprintLoc.is_selected) return 0;
            if (this.isImprintLocationFullColor(imprintLoc)) {
                return this.getSetupFeeMasterData("setup_fee_multicolor") || 0;
            }
            const colorCount = parseInt(imprintLoc.no_of_color) || 0;
            if (colorCount === 0) return 0;
            const feeStructure = this.getSetupFeeStructure();
            const feeStructNum = parseInt(feeStructure);
            if (feeStructNum === 1 || feeStructure === "1") {
                return (
                    this.getSetupFeeMasterData("setup_fee_color1") * colorCount
                );
            }
            if (feeStructNum === 2 || feeStructure === "2") {
                if (colorCount === 1) {
                    return this.getSetupFeeMasterData("setup_fee_color1");
                }
                return this.getSetupFeeMasterData("setup_fee_multicolor");
            }
            return 0;
        },
        appendSetupFeeRollupServices(total) {
            let t = total;
            const setupFeeServices = this.getServicesGroupedByRollupFee(2);
            setupFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    t += this.getServicePrice(service, this.quantity);
                });
            });
            return parseFloat(t.toFixed(2));
        },
        computeCalculatedSetupFeeTotal() {
            const suppress = this.designGroupSuppressEffective;
            const selectedOrdered = (this.imprintLocations || []).filter(
                (l) => l && l.is_selected
            );
            if (!suppress || selectedOrdered.length === 0) {
                let total = 0;
                const firstLocation =
                    this.imprintLocations &&
                    this.imprintLocations.find((loc) => loc.is_selected);
                if (firstLocation && firstLocation.is_selected) {
                    if (
                        this.isFirstLocationFullColor() ||
                        this.getFirstLocationColorCount() > 0
                    ) {
                        total += this.getLocation1SetupFee();
                    }
                }
                const additionalLocations = this.getAdditionalLocations();
                additionalLocations.forEach((location) => {
                    if (location.isFullColor === true || location.colorCount > 0) {
                        total += this.getAdditionalLocationSetupFee(location);
                    }
                });
                return this.appendSetupFeeRollupServices(total);
            }
            let totalGrouped = 0;
            const grouped = this.getPcnaLocationGroupingMetadata(
                selectedOrdered,
                this.designGroupSuppressEffective
            );
            let garmentFirstPrimarySetupCharged = false;
            grouped.forEach((entry) => {
                const imprintLoc = entry.location;
                const hasDeco =
                    this.isImprintLocationFullColor(imprintLoc) ||
                    (parseInt(imprintLoc.no_of_color) || 0) > 0;
                if (!hasDeco) {
                    return;
                }
                if (!entry.isGroupPrimary) {
                    return;
                }
                if (!garmentFirstPrimarySetupCharged) {
                    totalGrouped += this.getPrimaryStyleSetupFeeForImprint(
                        imprintLoc
                    );
                    garmentFirstPrimarySetupCharged = true;
                } else {
                    const wrapper =
                        this.buildAdditionalLocationWrapperFromImprint(
                            imprintLoc
                        );
                    if (
                        wrapper.isFullColor === true ||
                        wrapper.colorCount > 0
                    ) {
                        totalGrouped += this.getAdditionalLocationSetupFee(
                            wrapper
                        );
                    }
                }
            });
            return this.appendSetupFeeRollupServices(totalGrouped);
        },
        isAdditionalLocationSetupSuppressedByDesignGroup(locationWrapper) {
            if (!locationWrapper || !locationWrapper.location) return false;
            if (!this.designGroupSuppressEffective) {
                return false;
            }
            const selectedOrdered = (this.imprintLocations || []).filter(
                (l) => l && l.is_selected
            );
            const imprintRef = locationWrapper.location;
            if (this.suppressGroupByColorCountOnly) {
                return isImprintLocationSuppressedByColorCountOnly(
                    selectedOrdered,
                    imprintRef
                );
            }
            const grouped = this.getPcnaLocationGroupingMetadata(
                selectedOrdered,
                this.designGroupSuppressEffective
            );
            const entry = grouped.find((g) => g.location === imprintRef);
            if (!entry) return false;
            return !entry.isGroupPrimary;
        },
        imprintLocationEligibleForLocationRunCharge(imprintLoc) {
            if (!imprintLoc || !imprintLoc.is_selected) return false;
            if (this.isImprintLocationFullColor(imprintLoc)) return true;
            const n = parseInt(imprintLoc.no_of_color);
            return !isNaN(n) && n > 0;
        },
        getFullColorUnitForImprintLoc(imprintLoc) {
            if (imprintLoc && imprintLoc.full_color_value) {
                return parseFloat(imprintLoc.full_color_value) || 0;
            }
            return this.getFirstLocationFullColorValue();
        },
        getImprintAdditionalColorsCountForRun(imprintLoc) {
            if (!imprintLoc || !imprintLoc.no_of_color) return 0;
            if (
                imprintLoc.is_full_color_selected === true ||
                imprintLoc.no_of_color === "full_color"
            ) {
                return 0;
            }
            const colorCount = parseInt(imprintLoc.no_of_color) || 0;
            return colorCount > 1 ? colorCount - 1 : 0;
        },
        sumImprintCityRunChargeFirstBlockForImprint(imprintLoc) {
            if (!this.imprintLocationEligibleForLocationRunCharge(imprintLoc)) {
                return 0;
            }
            const qty = this.quantity;
            const decorationType = parseInt(this.decorationType) || 0;
            let total = 0;
            if (decorationType === 1 || decorationType === 5) {
                if (this.isImprintLocationFullColor(imprintLoc)) {
                    const fullColorValue =
                        this.getFullColorUnitForImprintLoc(imprintLoc);
                    if (fullColorValue > 0) {
                        total += fullColorValue * qty;
                    }
                }
                const firstLocationMasterCost =
                    this.getServiceMasterData("location_1st") || 0;
                if (firstLocationMasterCost > 0) {
                    total += firstLocationMasterCost * qty;
                }
                if (!this.isImprintLocationFullColor(imprintLoc)) {
                    const firstColorMasterCost =
                        this.getServiceMasterData("cost_per_color_1st") || 0;
                    if (firstColorMasterCost > 0) {
                        total += firstColorMasterCost * qty;
                    }
                    const addl = this.getImprintAdditionalColorsCountForRun(
                        imprintLoc
                    );
                    if (addl > 0) {
                        const firstLocationAddlPrice =
                            this.getFirstLocationAddlColorPrice();
                        if (firstLocationAddlPrice > 0) {
                            total += firstLocationAddlPrice * addl * qty;
                        }
                    }
                }
            } else if (decorationType === 2 || decorationType === 4) {
                if (this.isImprintLocationFullColor(imprintLoc)) {
                    const fullColorValue =
                        this.getFullColorUnitForImprintLoc(imprintLoc);
                    if (fullColorValue > 0) {
                        total += fullColorValue * qty;
                    }
                }
                const firstLocationMasterCost =
                    this.getServiceMasterData("location_1st") || 0;
                if (firstLocationMasterCost > 0) {
                    total += firstLocationMasterCost * qty;
                }
                if (!this.isImprintLocationFullColor(imprintLoc)) {
                    const firstColorMasterCost =
                        this.getServiceMasterData("cost_per_color_1st") || 0;
                    if (firstColorMasterCost > 0) {
                        total += firstColorMasterCost * qty;
                    }
                    const addl = this.getImprintAdditionalColorsCountForRun(
                        imprintLoc
                    );
                    const firstLocationAddlPrice =
                        this.getFirstLocationAddlColorPrice();
                    if (addl > 0 && firstLocationAddlPrice > 0) {
                        total += firstLocationAddlPrice * addl * qty;
                    }
                }
            }
            const locUp = this.getLocationColorUpcharge(imprintLoc) || 0;
            if (locUp > 0) {
                total += locUp * qty;
            }
            const custUp =
                this.getLocationCustomColorUpcharge(imprintLoc) || 0;
            if (custUp > 0) {
                total += custUp * qty;
            }
            const fees = this.getLocationNameNumberFees(imprintLoc);
            fees.forEach((fee) => {
                total += fee.total;
            });
            return total;
        },
        sumImprintCityRunChargeAdditionalBlockForWrapper(wrapper) {
            if (!wrapper) return 0;
            const qty = this.quantity;
            const decorationType = parseInt(this.decorationType) || 0;
            let total = 0;
            const additionalLocationBase =
                this.getServiceMasterData("additional_location") || 0;
            if (additionalLocationBase > 0) {
                total += additionalLocationBase * qty;
            }
            if (decorationType === 1 || decorationType === 5) {
                if (wrapper.isFullColor === true) {
                    const fullColorValue = this.getFullColorUnitForImprintLoc(
                        wrapper.location
                    );
                    if (fullColorValue > 0) {
                        total += fullColorValue * qty;
                    }
                } else if (wrapper.colorCount > 0) {
                    const loc2ColorCharge =
                        this.getType1SecondLocationAdditionalColorTotal(
                            wrapper
                        );
                    if (loc2ColorCharge > 0) {
                        total += loc2ColorCharge;
                    }
                }
            } else if (decorationType === 2 || decorationType === 4) {
                if (wrapper.isFullColor === true) {
                    const fullColorValue = this.getFullColorUnitForImprintLoc(
                        wrapper.location
                    );
                    if (fullColorValue > 0) {
                        total += fullColorValue * qty;
                    }
                } else if (wrapper.colorCount > 0) {
                    const embroideryPrice =
                        this.getEmbroideryLocationPrice(wrapper);
                    if (embroideryPrice > 0) {
                        total += embroideryPrice * qty;
                    }
                    const addlEmbroideryColorTotal =
                        this.getEmbroideryLocation2AdditionalColorTotal(
                            wrapper
                        );
                    if (addlEmbroideryColorTotal > 0) {
                        total += addlEmbroideryColorTotal;
                    }
                }
            }
            const imprintRef = wrapper.location;
            if (imprintRef) {
                const locationColorUpcharge =
                    this.getLocationColorUpcharge(imprintRef);
                if (locationColorUpcharge > 0) {
                    total += locationColorUpcharge * qty;
                }
                const locationCustomColorUpcharge =
                    this.getLocationCustomColorUpcharge(imprintRef);
                if (locationCustomColorUpcharge > 0) {
                    total += locationCustomColorUpcharge * qty;
                }
            }
            const nameNumberFees = this.getLocationNameNumberFees(imprintRef);
            nameNumberFees.forEach((fee) => {
                total += fee.total;
            });
            return total;
        },
        appendImprintCityRunChargeTailTotals(total) {
            let t = total;
            if (this.isEmbroideryService() && this.stitchCount > 0) {
                const stitchCost = this.getStitchCost();
                if (stitchCost > 0) {
                    t += stitchCost * this.quantity;
                }
            }
            const otherRunChargeDetails = this.getOtherRunChargeDetails();
            otherRunChargeDetails.forEach((detail) => {
                t += parseFloat(detail.value || 0);
            });
            const runChargeServices = this.getServicesGroupedByRollupFee(12);
            runChargeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    t += this.getServicePrice(service, this.quantity);
                });
            });
            return t;
        },
        computeCalculatedRunChargeTotalSuppress() {
            const selectedOrdered = (this.imprintLocations || []).filter(
                (l) => l && l.is_selected
            );
            let total = 0;
            const grouped = this.getPcnaLocationGroupingMetadata(
                selectedOrdered,
                this.designGroupSuppressEffective
            );
            let garmentFirstPrimaryCharged = false;
            grouped.forEach((entry) => {
                const imprintLoc = entry.location;
                if (!this.imprintLocationEligibleForLocationRunCharge(imprintLoc)) {
                    return;
                }
                if (!entry.isGroupPrimary) {
                    return;
                }
                if (!garmentFirstPrimaryCharged) {
                    total += this.sumImprintCityRunChargeFirstBlockForImprint(
                        imprintLoc
                    );
                    garmentFirstPrimaryCharged = true;
                } else {
                    const wrapper =
                        this.buildAdditionalLocationWrapperFromImprint(
                            imprintLoc
                        );
                    total += this.sumImprintCityRunChargeAdditionalBlockForWrapper(
                        wrapper
                    );
                }
            });
            return parseFloat(
                this.appendImprintCityRunChargeTailTotals(total).toFixed(2)
            );
        },
        formatPrice(value) {
            if (value === null || isNaN(value)) return "$0.00";
            return "$" + parseFloat(value).toFixed(2);
        },
        // Get color value (hex) from combination object
        getColorValue(combination) {
            if (!combination) return "#cccccc";
            // Try different possible property names: color_value, hex, hash
            let colorValue =
                combination.color_value ||
                combination.hex ||
                combination.hash ||
                (combination.color && combination.color.color_value) ||
                (combination.color && combination.color.hex) ||
                (combination.color && combination.color.hash) ||
                "";

            // Ensure color value has # prefix
            if (colorValue && !colorValue.startsWith("#")) {
                colorValue = "#" + colorValue;
            }

            return colorValue || "#cccccc";
        },
        // Get color name from combination object
        getColorName(combination) {
            if (!combination) return "N/A";
            // Try different possible property names
            return (
                combination.color_name ||
                combination.name ||
                (combination.color && combination.color.color_name) ||
                (combination.color && combination.color.name) ||
                "Color"
            );
        },
        // Get unit price for included services (show_service = false) for base fee
        getIncludedServicesUnitPrice() {
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let includedServicesUnitPrice = 0;

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === false) {
                        // Get unit price directly (retail_cost), don't divide by quantity
                        const serviceUnitPrice =
                            this.getServiceUnitPrice(service);
                        includedServicesUnitPrice += serviceUnitPrice;
                    }
                });
            });

            return parseFloat(includedServicesUnitPrice.toFixed(4));
        },
        // Get base unit price (including included services)
        getBaseUnitPrice() {
            let baseUnitPrice = 0;
            if (this.quantity > 0 && this.baseCost > 0) {
                baseUnitPrice = parseFloat(this.baseCost / this.quantity);
            }

            // Add included services unit price
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            return parseFloat(
                (baseUnitPrice + includedServicesUnitPrice).toFixed(2)
            );
        },
        // Get unit price for a color-size combination (including included services)
        getColorSizeUnitPrice(combination) {
            if (!combination) return 0;
            let baseUnitPrice = 0;

            // Use cost or total_cost divided by quantity
            if (combination.cost !== undefined) {
                baseUnitPrice = parseFloat(combination.cost) || 0;
            } else if (combination.total_cost && combination.quantity) {
                baseUnitPrice =
                    parseFloat(combination.total_cost) /
                        parseInt(combination.quantity) || 0;
            } else if (this.baseCost > 0 && this.quantity > 0) {
                // Fallback: calculate from baseCost and total quantity
                baseUnitPrice = parseFloat(
                    (this.baseCost / this.quantity).toFixed(2)
                );
            }

            // Add included services unit price
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            let finalUnitPrice = parseFloat(
                (baseUnitPrice + includedServicesUnitPrice).toFixed(2)
            );

            // Apply discount if activeProductDiscount exists
            return this.applyDiscountToPrice(finalUnitPrice);
        },
        // Apply discount to price if discountConfig is active
        applyDiscountToPrice(originalPrice) {
            if (!originalPrice || originalPrice <= 0) {
                return originalPrice;
            }

            if (!this.discountConfig || !this.discountConfig.status) {
                return originalPrice;
            }

            const discountType = this.discountConfig.discount_type;
            const discountValue = parseFloat(
                this.discountConfig.discount_value || 0
            );

            if (discountType === "Percent") {
                return parseFloat(
                    (originalPrice * (1 - discountValue / 100)).toFixed(2)
                );
            } else {
                // Fixed amount discount
                return parseFloat(
                    Math.max(0, originalPrice - discountValue).toFixed(2)
                );
            }
        },
        // Get total price for a color-size combination
        getColorSizeTotalPrice(combination) {
            if (!combination || !combination.quantity) return 0;
            const unitPrice = this.getColorSizeUnitPrice(combination);
            const qty = parseInt(combination.quantity) || 0;
            return parseFloat((unitPrice * qty).toFixed(2));
        },
        // Get base fee services (services with rollup_fee_id = 1)
        getBaseFeeServices() {
            if (!this.otherServices || !Array.isArray(this.otherServices)) {
                return [];
            }
            return this.otherServices.filter((service) => {
                const feeId =
                    service.rollup_service &&
                    service.rollup_service.rollup_fee_id;
                return feeId === 1;
            });
        },
        // Get services grouped by rollup fee ID and name for a specific fee ID
        getServicesGroupedByRollupFee(feeId) {
            if (!this.otherServices || this.otherServices.length === 0)
                return [];

            // Filter services by the specified fee_id
            // Exclude VendorFees services (identified by 'type' field: name, number, custom_ink_mix, thread_fee)
            const filteredServices = this.otherServices.filter((service) => {
                // Exclude VendorFees services - they come from VendorFees array and should not show in tooltip
                if (
                    service.type &&
                    (service.type === "name" ||
                        service.type === "number" ||
                        service.type === "custom_ink_mix" ||
                        service.type === "thread_fee")
                ) {
                    return false;
                }

                const rollupService = service.rollup_service || {};
                const serviceFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;
                return serviceFeeId === feeId;
            });

            if (filteredServices.length === 0) return [];

            // Group by rollup_fee_id (should all be the same fee_id, but grouping for consistency)
            const grouped = {};

            filteredServices.forEach((service) => {
                const rollupService = service.rollup_service || {};
                const rollupFeeId =
                    rollupService.rollup_fee_id !== undefined &&
                    rollupService.rollup_fee_id !== null
                        ? rollupService.rollup_fee_id
                        : service.rollup_fee_id !== undefined &&
                          service.rollup_fee_id !== null
                        ? service.rollup_fee_id
                        : null;

                if (rollupFeeId !== undefined && rollupFeeId !== null) {
                    if (!grouped[rollupFeeId]) {
                        // Get rollup fee name from rollup_service or service
                        const rollupFeeName =
                            rollupService.rollup_fee ||
                            service.rollup_fee_name ||
                            `Rollup Fee ${rollupFeeId}`;

                        grouped[rollupFeeId] = {
                            rollup_fee_id: rollupFeeId,
                            rollup_fee_name: rollupFeeName,
                            services: []
                        };
                    }
                    grouped[rollupFeeId].services.push(service);
                }
            });

            // Convert to array and sort by rollup_fee_id
            return Object.values(grouped).sort(
                (a, b) => a.rollup_fee_id - b.rollup_fee_id
            );
        },
        // Get total of services with show_service = false (INCL services)
        getIncludedServicesTotal() {
            const baseFeeServices = this.getServicesGroupedByRollupFee(1);
            let includedServicesTotal = 0;

            baseFeeServices.forEach((rollupGroup) => {
                rollupGroup.services.forEach((service) => {
                    const show =
                        service.show_service !== undefined
                            ? service.show_service
                            : true;
                    if (show === false) {
                        includedServicesTotal += this.getServicePrice(
                            service,
                            this.quantity
                        );
                    }
                });
            });

            return includedServicesTotal;
        },
        // Get Product Base Price total including INCL services
        getProductBasePriceTotal() {
            const includedServicesUnitPrice =
                this.getIncludedServicesUnitPrice();
            const includedServicesTotal =
                includedServicesUnitPrice * this.quantity;
            const baseTotal = parseFloat(
                (this.baseCost + includedServicesTotal).toFixed(2)
            );

            // Apply discount if activeProductDiscount exists
            return this.applyDiscountToPrice(baseTotal);
        },
        // Get service price
        getServicePrice(service, quantity = 1) {
            const cost = parseFloat(service.retail_cost || service.cost || 0);
            const rollupService = service.rollup_service || {};
            if (
                service.fee_structure === "Per Item" ||
                rollupService.fee_structure_name === "Per Item"
            ) {
                return cost * quantity;
            }
            return cost;
        },
        // Get service unit price
        getServiceUnitPrice(service) {
            return parseFloat(service.retail_cost || service.cost || 0);
        },
        // Get vendor service for rush fee calculation
        // Checks if the service is available and has pricing
        // otherServices already contains vendor service data, so we just need to check if it has retail_cost
        getVendorServiceForRush(service) {
            if (!service) {
                return null;
            }

            // Check if service has retail_cost (indicates vendor service is available)
            // If retail_cost is 0 or null, service is not available or has no price
            // Note: retail_cost is checked but not stored as it's only used for validation

            // Return service if it has a valid retail_cost
            // Note: We still return the service even if cost is 0, to show it in tooltip
            // But we won't add it to the total if cost is 0
            return service;
        },
        // Get fee structure value (numeric ID)
        // Returns 6 for "Per Item", otherwise returns the fee_structure value
        getServiceFeeStructureValue(service) {
            if (!service) return null;

            // Check if fee_structure is "Per Item" (string) or 6 (numeric)
            const feeStructure = service.fee_structure;
            const rollupService = service.rollup_service || {};

            if (
                feeStructure === "Per Item" ||
                feeStructure === 6 ||
                rollupService.fee_structure_name === "Per Item"
            ) {
                return 6; // Per Item
            }

            // Return the fee_structure value if it's numeric, otherwise return null
            return typeof feeStructure === "number" ? feeStructure : null;
        },
        // Get service name safely
        getServiceName(service) {
            if (!service) return "Service";

            // Try to get ecomm display name for other services
            const otherServiceEcommDisplayName =
                this.getOtherServiceEcommDisplayName(service);
            if (otherServiceEcommDisplayName) {
                return otherServiceEcommDisplayName;
            }

            // Try master_service_id for direct lookup
            if (service.master_service_id) {
                const ecommDisplayName = this.getEcommDisplayName(
                    service.master_service_id
                );
                if (ecommDisplayName) return ecommDisplayName;
            }

            // Fallback to service.name
            if (service.name) {
                return service.name;
            }

            // Fallback to master_service.service_name
            if (service.master_service && service.master_service.service_name) {
                return service.master_service.service_name;
            }

            // Fallback to ecomm_display
            if (service.ecomm_display) {
                return service.ecomm_display;
            }

            return "Service";
        },
        // Get rush service name with delivery date
        getRushServiceNameWithDate(serviceName) {
            if (!serviceName) {
                return serviceName;
            }

            // First check if selectedRushOptionDate is available (from Design Studio)
            if (this.selectedRushOptionDate && this.selectedRushOptionId) {
                // Check if this service matches the selected rush option
                const normalizeName = (name) => {
                    if (!name) return "";
                    return name
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, " ")
                        .replace(/day$/i, "day")
                        .replace(/days$/i, "day");
                };

                const rushOptionLabels = {
                    rush1: "Rush 1 Day",
                    rush3: "Rush 3 Day",
                    rush5: "Rush 5 Day",
                    rush7: "Rush 7 Day",
                    rush_package: "Rush Package"
                };

                const selectedLabel =
                    rushOptionLabels[this.selectedRushOptionId];
                if (selectedLabel) {
                    const normalizedServiceName = normalizeName(serviceName);
                    const normalizedLabel = normalizeName(selectedLabel);

                    // Check if service name matches selected rush option
                    if (
                        normalizedLabel === normalizedServiceName ||
                        normalizedServiceName.includes(normalizedLabel) ||
                        normalizedLabel.includes(normalizedServiceName)
                    ) {
                        // Format date as MM/DD/YYYY
                        // selectedRushOptionDate is stored as ISO string, convert to Date
                        const date = new Date(this.selectedRushOptionDate);
                        // Check if date is valid
                        if (!isNaN(date.getTime())) {
                            const mm = String(date.getMonth() + 1).padStart(
                                2,
                                "0"
                            );
                            const dd = String(date.getDate()).padStart(2, "0");
                            const yyyy = date.getFullYear();
                            const formattedDate = `${mm}/${dd}/${yyyy}`;
                            return `${serviceName} (${formattedDate})`;
                        }
                    }
                }
            }

            // Fallback to dynamicRushOptions (for Product Details)
            if (this.dynamicRushOptions && this.dynamicRushOptions.length > 0) {
                // Normalize service name for matching (remove extra spaces, handle variations)
                const normalizeName = (name) => {
                    if (!name) return "";
                    return name
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, " ")
                        .replace(/day$/i, "day")
                        .replace(/days$/i, "day");
                };

                const normalizedServiceName = normalizeName(serviceName);

                // Find matching rush option by label
                const rushOption = this.dynamicRushOptions.find((option) => {
                    if (!option.label) return false;
                    const normalizedLabel = normalizeName(option.label);
                    // Check exact match or if service name contains rush option label
                    return (
                        normalizedLabel === normalizedServiceName ||
                        normalizedServiceName.includes(normalizedLabel) ||
                        normalizedLabel.includes(normalizedServiceName)
                    );
                });

                if (rushOption && rushOption.estimatedDate) {
                    // Format date as MM/DD/YYYY
                    const date = new Date(rushOption.estimatedDate);
                    const mm = String(date.getMonth() + 1).padStart(2, "0");
                    const dd = String(date.getDate()).padStart(2, "0");
                    const yyyy = date.getFullYear();
                    const formattedDate = `${mm}/${dd}/${yyyy}`;
                    return `${serviceName} (${formattedDate})`;
                }
            }

            return serviceName;
        },
        // Get first location data
        getFirstLocationData() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return null;
            }
            const firstLocation =
                this.imprintLocations.find((loc) => loc.is_selected) ||
                this.imprintLocations[0];
            if (!firstLocation) return null;
            // Check if full color is selected or has color count
            if (
                firstLocation.is_full_color_selected === true ||
                firstLocation.no_of_color === "full_color" ||
                parseInt(firstLocation.no_of_color) > 0
            ) {
                return firstLocation;
            }
            return null;
        },
        // Check if first location has full color selected
        isFirstLocationFullColor() {
            const firstLocation = this.getFirstLocationData();
            if (!firstLocation) {
                return false;
            }
            // Check all possible full color indicators
            // If no_of_color is a number (not "full_color"), it's not full color
            if (
                firstLocation.no_of_color &&
                firstLocation.no_of_color !== "full_color"
            ) {
                const colorNum = parseInt(firstLocation.no_of_color);
                if (!isNaN(colorNum)) {
                    // It's a number, so it's not full color
                    return false;
                }
            }
            return (
                firstLocation.is_full_color_selected === true ||
                firstLocation.is_full_color === true ||
                firstLocation.no_of_color === "full_color"
            );
        },
        // Get full color value for first location
        getFirstLocationFullColorValue() {
            const firstLocation = this.getFirstLocationData();

            // First check if full_color_value is set on location object
            if (firstLocation && firstLocation.full_color_value) {
                return parseFloat(firstLocation.full_color_value) || 0;
            }
            // Check activeDecorationService for full_color value (this is where it's stored in decoration JSON)
            if (
                this.activeDecorationService &&
                this.activeDecorationService.full_color
            ) {
                return parseFloat(this.activeDecorationService.full_color) || 0;
            }
            // If not, check serviceMasterData for full_color value
            if (this.serviceMasterData && this.serviceMasterData.full_color) {
                return parseFloat(this.serviceMasterData.full_color) || 0;
            }
            // Also check runChargeDetails for full color value
            if (this.runChargeDetails && Array.isArray(this.runChargeDetails)) {
                const fullColorDetail = this.runChargeDetails.find(
                    (detail) =>
                        detail.name &&
                        detail.name.toLowerCase().includes("full color") &&
                        detail.unitPrice
                );
                if (fullColorDetail && fullColorDetail.unitPrice) {
                    return parseFloat(fullColorDetail.unitPrice) || 0;
                }
            }
            return 0;
        },
        // Get first location name
        getFirstLocationName() {
            const firstLocation = this.getFirstLocationData();
            if (firstLocation && firstLocation.location) {
                return firstLocation.location;
            }
            return "1st Location";
        },
        // Get first location additional color price (for 1st location, additional colors)
        getFirstLocationAddlColorPrice() {
            const decorationType = parseInt(this.decorationType) || 0;
            // Calculate the price difference between current color count and first color
            // For ImprintCity (decoration_type 1), this is the difference between color prices from print_location1
            if (
                !this.decorationPricingData ||
                !this.imprintLocations ||
                this.imprintLocations.length === 0
            ) {
                return 0;
            }

            const firstLocation = this.getFirstLocationData();
            if (!firstLocation || !firstLocation.no_of_color) {
                return 0;
            }

            const colorCount = parseInt(firstLocation.no_of_color) || 0;
            if (colorCount <= 1) {
                return 0;
            }

            // For decoration_type 1 (Print), use print_location1
            if (
                (decorationType === 1 || decorationType === 5) &&
                this.decorationPricingData.print_location1
            ) {
                const qty = this.totalQuantity || this.quantity;
                const priceData = this.decorationPricingData.print_location1;

                // Find the price tier for current quantity
                let lastColorPrice = 0;
                let firstColorPrice = 0;

                for (let i = priceData.length - 1; i >= 0; i--) {
                    const tier = priceData[i];
                    if (
                        tier &&
                        tier.quantity &&
                        parseInt(tier.quantity) <= qty
                    ) {
                        lastColorPrice = parseFloat(
                            tier[`colour${colorCount}`] || 0
                        );
                        firstColorPrice = parseFloat(tier.colour1 || 0);
                        break;
                    }
                }

                // If no tier found, use first tier
                if (lastColorPrice === 0 && priceData.length > 0) {
                    const firstTier = priceData[0];
                    lastColorPrice = parseFloat(
                        firstTier[`colour${colorCount}`] || 0
                    );
                    firstColorPrice = parseFloat(firstTier.colour1 || 0);
                }

                // Additional color price is the difference + master addl color (per additional color)
                const templateDelta = Math.max(
                    0,
                    lastColorPrice - firstColorPrice
                );
                const masterAddlColor =
                    this.getServiceMasterData("cost_per_color_2ndormore") || 0;
                return Math.max(0, templateDelta + masterAddlColor);
            }

            // For decoration_type 2/4 (Embroidery), addl color is direct master addl-color charge
            if (decorationType === 2 || decorationType === 4) {
                return (
                    parseFloat(
                        this.getServiceMasterData("cost_per_color_2ndormore")
                    ) || 0
                );
            }

            return 0;
        },
        // Get additional location additional color price (direct price for additional location)
        getAdditionalLocationAddlColorPrice(location) {
            const decorationType = parseInt(this.decorationType) || 0;
            // For additional locations, use the direct price from print_location2 (for Print) or decoration bulk (for Embroidery)
            // This matches UseImprintCalculator.getSecondLocationDirectPrice() logic
            if (
                !location ||
                !location.colorCount ||
                location.colorCount === 0
            ) {
                return 0;
            }

            if (!this.decorationPricingData) {
                return 0;
            }

            // For decoration_type 1 (Print), use print_location2
            if (
                (decorationType === 1 || decorationType === 5) &&
                this.decorationPricingData.print_location2
            ) {
                const qty = this.totalQuantity || this.quantity;
                const priceData = this.decorationPricingData.print_location2;

                // Find the price tier for current quantity
                let directPrice = 0;

                for (let i = priceData.length - 1; i >= 0; i--) {
                    const tier = priceData[i];
                    if (
                        tier &&
                        tier.quantity &&
                        parseInt(tier.quantity) <= qty
                    ) {
                        // For additional location, use the direct price for the color count
                        // This is the price per color for additional locations
                        directPrice = parseFloat(
                            tier[`colour${location.colorCount}`] || 0
                        );
                        break;
                    }
                }

                // If no tier found, use first tier
                if (directPrice === 0 && priceData.length > 0) {
                    const firstTier = priceData[0];
                    directPrice = parseFloat(
                        firstTier[`colour${location.colorCount}`] || 0
                    );
                }

                return directPrice;
            }

            return 0;
        },
        // Get embroidery location price (for decoration_type 2)
        getEmbroideryLocationPrice(location) {
            // For embroidery, use location2 price from decoration bulk data
            // Matches UseImprintCalculator.calculateRunCharge() for decoration_type 2
            if (
                !location ||
                !location.colorCount ||
                location.colorCount === 0
            ) {
                return 0;
            }

            if (!this.decorationPricingData) {
                return 0;
            }

            // For decoration_type 2 (Embroidery), use decoration bulk array
            const decorationType = parseInt(this.decorationType) || 0;
            if (decorationType === 2 || decorationType === 4) {
                const qty = this.totalQuantity || this.quantity;
                let priceData = this.decorationPricingData;

                // Handle both array and object formats
                if (Array.isArray(priceData)) {
                    // Find the price tier for current quantity
                    let selectedRow = null;

                    for (const row of priceData) {
                        if (row.quantity1 && row.quantity2) {
                            if (
                                qty >= parseInt(row.quantity1) &&
                                qty <= parseInt(row.quantity2)
                            ) {
                                selectedRow = row;
                                break;
                            }
                        } else if (
                            row.quantity1 &&
                            qty >= parseInt(row.quantity1)
                        ) {
                            selectedRow = row;
                            break;
                        }
                    }

                    // If no tier found, use first tier
                    if (!selectedRow && priceData.length > 0) {
                        selectedRow = priceData[0];
                    }

                    if (selectedRow && selectedRow.location2 !== undefined) {
                        return parseFloat(selectedRow.location2 || 0);
                    }
                }
            }

            return 0;
        },
        // Get stitch cost for embroidery
        getStitchCost() {
            if (!this.serviceMasterData || !this.isEmbroideryService()) {
                return 0;
            }

            // Get stitch cost from service master data
            // Common field names: emb_1000_over_10k, stitch_cost, etc.
            const stitchCost = parseFloat(
                this.serviceMasterData.emb_1000_over_10k ||
                    this.serviceMasterData.stitch_cost ||
                    this.serviceMasterData.embroidery_stitch_cost ||
                    0
            );

            if (stitchCost > 0 && this.stitchCount > 10000) {
                // Calculate cost for stitches over 10k
                const remainingStitches = this.stitchCount - 10000;
                const costPer1000Stitches = stitchCost;
                return (
                    Math.ceil(remainingStitches / 1000) * costPer1000Stitches
                );
            }

            return 0;
        },
        // Get first location additional colors count
        getFirstLocationAdditionalColors() {
            const firstLocation = this.getFirstLocationData();
            if (firstLocation && firstLocation.no_of_color) {
                // If full color is selected, no additional colors
                if (
                    firstLocation.is_full_color_selected === true ||
                    firstLocation.no_of_color === "full_color"
                ) {
                    return 0;
                }
                const colorCount = parseInt(firstLocation.no_of_color) || 0;
                return colorCount > 1 ? colorCount - 1 : 0;
            }
            return 0;
        },
        // Check if service is embroidery
        isEmbroideryService() {
            if (!this.serviceMasterData) return false;
            const serviceName = (
                this.serviceMasterData.service_name || ""
            ).toLowerCase();
            return (
                serviceName.includes("embroidery") ||
                serviceName.includes("emb") ||
                serviceName.includes("patch")
            );
        },
        // Get color ink mix for first location
        getFirstLocationColorInkMix() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return [];
            }
            // Find first selected location
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) {
                // Fallback to first location in array
                return this.getLocationColorInkMix(this.imprintLocations[0]);
            }
            return this.getLocationColorInkMix(firstLocation);
        },
        // Get color ink mix for a specific location
        getLocationColorInkMix(location) {
            if (!location) {
                return [];
            }

            // Check if colors array exists
            const colorsArray = location.colors || [];
            if (!Array.isArray(colorsArray) || colorsArray.length === 0) {
                return [];
            }

            const inkMixColors = [];
            colorsArray.forEach((colorObj) => {
                // Check if color object exists and has the right structure
                if (!colorObj) return;

                const color = colorObj.color;
                if (!color) return;

                // Check if it's a custom color (color_id == 0) with non_standard_fee
                const colorId = parseInt(color.color_id);
                const nonStandardFee = parseFloat(color.non_standard_fee || 0);

                if (colorId === 0 && nonStandardFee > 0) {
                    inkMixColors.push({
                        colorName:
                            color.color_name ||
                            color.colorName ||
                            "Custom Color",
                        fee: nonStandardFee
                    });
                }
            });
            return inkMixColors;
        },
        // Get total custom color fee for first location (sum of all mix fees)
        getFirstLocationTotalColorMixFee() {
            const colorMix = this.getFirstLocationColorInkMix();
            if (!colorMix || colorMix.length === 0) {
                return 0;
            }
            // Sum all mix fees
            const totalFee = colorMix.reduce((sum, colorData) => {
                return sum + parseFloat(colorData.fee || 0);
            }, 0);
            return parseFloat(totalFee.toFixed(2));
        },
        // Get total custom color fee for a specific location (sum of all mix fees)
        getLocationTotalColorMixFee(location) {
            if (!location) {
                return 0;
            }
            const colorMix = this.getLocationColorInkMix(location);
            if (!colorMix || colorMix.length === 0) {
                return 0;
            }
            // Sum all mix fees
            const totalFee = colorMix.reduce((sum, colorData) => {
                return sum + parseFloat(colorData.fee || 0);
            }, 0);
            return parseFloat(totalFee.toFixed(2));
        },
        // Get color upcharge for first location
        getFirstLocationColorUpcharge() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return 0;
            }
            // Find first selected location
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) {
                // Fallback to first location in array
                return this.getLocationColorUpcharge(this.imprintLocations[0]);
            }
            return this.getLocationColorUpcharge(firstLocation);
        },
        // Get color upcharge for a specific location
        getLocationColorUpcharge(location) {
            if (
                !location ||
                !this.vendorColors ||
                !Array.isArray(this.vendorColors)
            ) {
                return 0;
            }

            // Check if colors array exists
            const colorsArray = location.colors || [];
            if (!Array.isArray(colorsArray) || colorsArray.length === 0) {
                return 0;
            }

            let totalUpcharge = 0;
            colorsArray.forEach((colorObj) => {
                // Check if color object exists and has the right structure
                if (!colorObj || !colorObj.color) return;

                const color = colorObj.color;
                const colorValue = color.color_value || color.hex || "";
                if (!colorValue) return;

                // Find matching vendor color
                const vendorColor = this.vendorColors.find(
                    (vc) =>
                        vc.color_value &&
                        vc.color_value.toLowerCase() ===
                            colorValue.toLowerCase()
                );

                if (vendorColor && vendorColor.decor_upcharge) {
                    const upcharge = parseFloat(
                        vendorColor.decor_upcharge || 0
                    );
                    const stock =
                        vendorColor.stock === true || vendorColor.stock === 1;
                    const colorType = String(
                        (vendorColor.Color && vendorColor.Color.type) ||
                            vendorColor.color_type ||
                            ""
                    )
                        .replace(/['"]/g, "")
                        .trim()
                        .toLowerCase();
                    // Only include specialty colors: stock = true AND upcharge > 0
                    if (upcharge > 0 && stock && colorType !== "vinyl") {
                        totalUpcharge += upcharge;
                    }
                }
            });

            return parseFloat(totalUpcharge.toFixed(2));
        },
        getLocationCustomColorUpcharge(location) {
            if (
                !location ||
                !this.vendorColors ||
                !Array.isArray(this.vendorColors)
            ) {
                return 0;
            }

            // Check if colors array exists
            const colorsArray = location.colors || [];
            if (!Array.isArray(colorsArray) || colorsArray.length === 0) {
                return 0;
            }

            let totalUpcharge = 0;
            colorsArray.forEach((colorObj) => {
                // Check if color object exists and has the right structure
                if (!colorObj || !colorObj.color) return;

                const color = colorObj.color;
                const colorValue = color.color_value || color.hex || "";
                if (!colorValue) return;

                // Find matching vendor color
                const vendorColor = this.vendorColors.find(
                    (vc) =>
                        vc.color_value &&
                        vc.color_value.toLowerCase() ===
                            colorValue.toLowerCase()
                );

                if (vendorColor && vendorColor.decor_upcharge) {
                    const upcharge = parseFloat(
                        vendorColor.decor_upcharge || 0
                    );
                    const stock =
                        vendorColor.stock === true || vendorColor.stock === 1;
                    const colorType = String(
                        (vendorColor.Color && vendorColor.Color.type) ||
                            vendorColor.color_type ||
                            ""
                    )
                        .replace(/['"]/g, "")
                        .trim()
                        .toLowerCase();
                    // Only include custom colors: stock = false AND upcharge > 0
                    if (upcharge > 0 && !stock && colorType !== "vinyl") {
                        totalUpcharge += upcharge;
                    }
                }
            });

            return parseFloat(totalUpcharge.toFixed(2));
        },
        getFirstLocationCustomColorUpcharge() {
            if (!this.imprintLocations || this.imprintLocations.length === 0) {
                return 0;
            }
            // Find first selected location
            const firstLocation = this.imprintLocations.find(
                (loc) => loc.is_selected
            );
            if (!firstLocation) {
                // Fallback to first location in array
                return this.getLocationCustomColorUpcharge(
                    this.imprintLocations[0]
                );
            }
            return this.getLocationCustomColorUpcharge(firstLocation);
        },
        // Get order-level mix fee data (one-time, per order)
        getOrderMixFeeData() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return null;
            }

            // Find Custom Color Ink Fee or Custom Color Thread Fee in runChargeDetails
            const mixFeeDetail = this.runChargeDetails.find((detail) => {
                if (!detail || !detail.name) return false;
                const nameLower = detail.name.toLowerCase();
                return (
                    (nameLower.includes("custom color ink fee") ||
                        nameLower.includes("custom color thread fee") ||
                        nameLower.includes("custom color fee") ||
                        nameLower.includes("color ink mix fee") ||
                        nameLower.includes("color thread mix fee")) &&
                    !nameLower.includes("decor up")
                );
            });

            if (!mixFeeDetail) {
                return null;
            }

            // Get mix color count and unit price from the detail object
            const mixColorCount = mixFeeDetail.mixColorCount || 0;
            const unitPrice = mixFeeDetail.unitPrice || 0;
            const total = parseFloat(mixFeeDetail.value || 0);

            if (total <= 0) {
                return null;
            }

            return {
                mixColorCount: mixColorCount,
                unitPrice: unitPrice,
                total: total
            };
        },
        // Get Name Fee from VendorFees (using ecomm_display for matching)
        getNameFeeFromVendorServices() {
            if (!this.otherServices || !Array.isArray(this.otherServices)) {
                return null;
            }
            // First try to find by type (VendorFees structure)
            let nameService = this.otherServices.find(
                (service) => service.type === "name"
            );
            // If not found, try ecomm_display matching
            if (!nameService) {
                nameService = this.otherServices.find(
                    (service) =>
                        (service.ecomm_display &&
                            service.ecomm_display
                                .toLowerCase()
                                .includes("name")) ||
                        (service.name &&
                            service.name.toLowerCase().includes("name fees"))
                );
            }
            if (nameService && nameService.retail_cost) {
                return parseFloat(nameService.retail_cost) || 0;
            }
            return null;
        },
        // Get Number Fee from VendorFees (using ecomm_display for matching)
        getNumberFeeFromVendorServices() {
            if (!this.otherServices || !Array.isArray(this.otherServices)) {
                return null;
            }
            // First try to find by type (VendorFees structure)
            let numberService = this.otherServices.find(
                (service) => service.type === "number"
            );
            // If not found, try ecomm_display matching
            if (!numberService) {
                numberService = this.otherServices.find(
                    (service) =>
                        (service.ecomm_display &&
                            service.ecomm_display
                                .toLowerCase()
                                .includes("number")) ||
                        (service.name &&
                            service.name.toLowerCase().includes("number fees"))
                );
            }
            if (numberService && numberService.retail_cost) {
                return parseFloat(numberService.retail_cost) || 0;
            }
            return null;
        },
        // Get unique non-standard color count from imprint locations
        getUniqueNonStandardColorCount() {
            if (
                !this.imprintLocations ||
                !Array.isArray(this.imprintLocations)
            ) {
                return 0;
            }
            let uniqueColors = new Set();
            this.imprintLocations.forEach((location) => {
                if (
                    location.is_selected &&
                    location.colors &&
                    Array.isArray(location.colors)
                ) {
                    location.colors.forEach((colorObj) => {
                        if (colorObj && colorObj.color) {
                            const color = colorObj.color;
                            // Check if it's a custom color (color_id == 0)
                            if (parseInt(color.color_id) === 0) {
                                const hex =
                                    color.color_value || color.hex || "";
                                if (hex) {
                                    uniqueColors.add(hex.toLowerCase());
                                }
                            }
                        }
                    });
                }
            });
            return uniqueColors.size;
        },
        // Get name/number fees for first location (Front) - order: Name Fee, Number Fee, Name Decor Up, Number Decor Up
        getFirstLocationNameNumberFees() {
            const fees = [];

            // Try to get from runChargeDetails first (for backward compatibility)
            if (this.runChargeDetails && Array.isArray(this.runChargeDetails)) {
                // Front Name Fee
                const frontNameFee = this.runChargeDetails.find(
                    (d) =>
                        d.name &&
                        d.name.toLowerCase().includes("front name fee") &&
                        !d.name.toLowerCase().includes("decor up")
                );
                if (frontNameFee && parseFloat(frontNameFee.value || 0) > 0) {
                    const perItemFee = parseFloat(frontNameFee.unitPrice || 0);
                    const quantity = parseFloat(frontNameFee.quantity || 0);
                    const total = parseFloat(frontNameFee.value || 0);
                    fees.push({
                        name: frontNameFee.name,
                        perItemFee: perItemFee,
                        quantity: quantity,
                        total: total
                    });
                }
                // Front Number Fee
                const frontNumberFee = this.runChargeDetails.find(
                    (d) =>
                        d.name &&
                        d.name.toLowerCase().includes("front number fee") &&
                        !d.name.toLowerCase().includes("decor up")
                );
                if (
                    frontNumberFee &&
                    parseFloat(frontNumberFee.value || 0) > 0
                ) {
                    // Use unitPrice and quantity from runChargeDetails (frontNumberCount is already in quantity)
                    const perItemFee = parseFloat(
                        frontNumberFee.unitPrice || 0
                    );
                    const quantity = parseFloat(frontNumberFee.quantity || 0); // This is frontNumberCount from ItemDetails
                    const total = parseFloat(frontNumberFee.value || 0);
                    fees.push({
                        name: frontNumberFee.name,
                        perItemFee: perItemFee,
                        quantity: quantity,
                        total: total
                    });
                }
            }

            // Front Name Decor Up Fee (from runChargeDetails)
            if (this.runChargeDetails && Array.isArray(this.runChargeDetails)) {
                const frontNameDecorUp = this.runChargeDetails.find(
                    (d) =>
                        d.name &&
                        d.name.toLowerCase().includes("front name decor up fee")
                );
                if (
                    frontNameDecorUp &&
                    parseFloat(frontNameDecorUp.value || 0) > 0
                ) {
                    const frontNameFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("front name fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    const quantity = frontNameFee
                        ? parseFloat(frontNameFee.quantity || 0)
                        : this.quantity;
                    let perItemFee = 0;
                    if (frontNameDecorUp.unitPrice) {
                        perItemFee = parseFloat(
                            frontNameDecorUp.unitPrice || 0
                        );
                    } else if (quantity > 0) {
                        perItemFee =
                            parseFloat(frontNameDecorUp.value || 0) / quantity;
                    } else {
                        perItemFee = parseFloat(frontNameDecorUp.value || 0);
                    }
                    const total = perItemFee * quantity;
                    fees.push({
                        name: frontNameDecorUp.name,
                        perItemFee: perItemFee,
                        quantity: quantity,
                        total: total
                    });
                }
            }
            // Front Number Decor Up Fee (still from runChargeDetails)
            if (this.runChargeDetails && Array.isArray(this.runChargeDetails)) {
                const frontNumberDecorUp = this.runChargeDetails.find(
                    (d) =>
                        d.name &&
                        d.name
                            .toLowerCase()
                            .includes("front number decor up fee")
                );
                if (
                    frontNumberDecorUp &&
                    parseFloat(frontNumberDecorUp.value || 0) > 0
                ) {
                    // Get the quantity from Front Number Fee (not product quantity)
                    const frontNumberFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("front number fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    const quantity = frontNumberFee
                        ? parseFloat(frontNumberFee.quantity || 0)
                        : this.quantity;
                    // Calculate per-item fee: if unitPrice exists use it, otherwise divide total value by quantity
                    let perItemFee = 0;
                    if (frontNumberDecorUp.unitPrice) {
                        perItemFee = parseFloat(
                            frontNumberDecorUp.unitPrice || 0
                        );
                    } else if (quantity > 0) {
                        // Value is total, divide by quantity to get per-item price
                        perItemFee =
                            parseFloat(frontNumberDecorUp.value || 0) /
                            quantity;
                    } else {
                        perItemFee = parseFloat(frontNumberDecorUp.value || 0);
                    }
                    const total = perItemFee * quantity;
                    fees.push({
                        name: frontNumberDecorUp.name,
                        perItemFee: perItemFee,
                        quantity: quantity,
                        total: total
                    });
                }
            }

            return fees;
        },
        // Get name/number fees for a specific location (Back, Left, Right)
        getLocationNameNumberFees(location) {
            if (!location) {
                return [];
            }
            // location may be { locationName: "Back", location: imprintObj } from getAdditionalLocations
            const rawLocationName = (
                location.locationName ||
                (typeof location.location === "string"
                    ? location.location
                    : "") ||
                ""
            ).toLowerCase();

            // Normalize compound names like "left sleeve", "right sleeve" etc. to core locations
            let locationName = rawLocationName.trim();
            if (locationName.includes("front")) {
                locationName = "front";
            } else if (locationName.includes("back")) {
                locationName = "back";
            } else if (locationName.includes("left")) {
                locationName = "left";
            } else if (locationName.includes("right")) {
                locationName = "right";
            }
            const fees = [];

            // Back Name Fee (for Back location)
            if (locationName === "back") {
                // Try to get from runChargeDetails first
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNameFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("back name fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    if (backNameFee && parseFloat(backNameFee.value || 0) > 0) {
                        // Use unitPrice and quantity from runChargeDetails (backNameCount is already in quantity)
                        const perItemFee = parseFloat(
                            backNameFee.unitPrice || 0
                        );
                        const quantity = parseFloat(backNameFee.quantity || 0); // This is backNameCount from ItemDetails
                        const total = parseFloat(backNameFee.value || 0);
                        fees.push({
                            name: backNameFee.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }

                // Note: Fallback calculation removed - fees should always come from runChargeDetails
                // which is calculated based on actual names/numbers count, not color count
                // If runChargeDetails doesn't have the fee, it means there are no names/numbers added

                // Back Number Fee (for Back location)
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNumberFee = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes("back number fee") &&
                            !d.name.toLowerCase().includes("decor up")
                    );
                    if (
                        backNumberFee &&
                        parseFloat(backNumberFee.value || 0) > 0
                    ) {
                        // Use unitPrice and quantity from runChargeDetails (backNumberCount is already in quantity)
                        const perItemFee = parseFloat(
                            backNumberFee.unitPrice || 0
                        );
                        const quantity = parseFloat(
                            backNumberFee.quantity || 0
                        ); // This is backNumberCount from ItemDetails
                        const total = parseFloat(backNumberFee.value || 0);
                        fees.push({
                            name: backNumberFee.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }

                // Note: Fallback calculation removed - fees should always come from runChargeDetails
                // which is calculated based on actual names/numbers count, not color count
                // If runChargeDetails doesn't have the fee, it means there are no names/numbers added

                // Back Name Decor Up Fee (still from runChargeDetails)
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNameDecorUp = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name
                                .toLowerCase()
                                .includes("back name decor up fee")
                    );
                    if (
                        backNameDecorUp &&
                        parseFloat(backNameDecorUp.value || 0) > 0
                    ) {
                        // Get the quantity from Back Name Fee (not product quantity)
                        const backNameFee = this.runChargeDetails.find(
                            (d) =>
                                d.name &&
                                d.name
                                    .toLowerCase()
                                    .includes("back name fee") &&
                                !d.name.toLowerCase().includes("decor up")
                        );
                        const quantity = backNameFee
                            ? parseFloat(backNameFee.quantity || 0)
                            : this.quantity;
                        // Calculate per-item fee: if unitPrice exists use it, otherwise divide total value by quantity
                        let perItemFee = 0;
                        if (backNameDecorUp.unitPrice) {
                            perItemFee = parseFloat(
                                backNameDecorUp.unitPrice || 0
                            );
                        } else if (quantity > 0) {
                            // Value is total, divide by quantity to get per-item price
                            perItemFee =
                                parseFloat(backNameDecorUp.value || 0) /
                                quantity;
                        } else {
                            perItemFee = parseFloat(backNameDecorUp.value || 0);
                        }
                        const total = perItemFee * quantity;
                        fees.push({
                            name: backNameDecorUp.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }

                // Back Number Decor Up Fee (still from runChargeDetails)
                if (
                    this.runChargeDetails &&
                    Array.isArray(this.runChargeDetails)
                ) {
                    const backNumberDecorUp = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name
                                .toLowerCase()
                                .includes("back number decor up fee")
                    );
                    if (
                        backNumberDecorUp &&
                        parseFloat(backNumberDecorUp.value || 0) > 0
                    ) {
                        // Get the quantity from Back Number Fee (not product quantity)
                        const backNumberFee = this.runChargeDetails.find(
                            (d) =>
                                d.name &&
                                d.name
                                    .toLowerCase()
                                    .includes("back number fee") &&
                                !d.name.toLowerCase().includes("decor up")
                        );
                        const quantity = backNumberFee
                            ? parseFloat(backNumberFee.quantity || 0)
                            : this.quantity;
                        // Calculate per-item fee: if unitPrice exists use it, otherwise divide total value by quantity
                        let perItemFee = 0;
                        if (backNumberDecorUp.unitPrice) {
                            perItemFee = parseFloat(
                                backNumberDecorUp.unitPrice || 0
                            );
                        } else if (quantity > 0) {
                            // Value is total, divide by quantity to get per-item price
                            perItemFee =
                                parseFloat(backNumberDecorUp.value || 0) /
                                quantity;
                        } else {
                            perItemFee = parseFloat(
                                backNumberDecorUp.value || 0
                            );
                        }
                        const total = perItemFee * quantity;
                        fees.push({
                            name: backNumberDecorUp.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: total
                        });
                    }
                }
            }

            // Left Name/Number Fees (same pattern as Back)
            if (
                locationName === "left" &&
                this.runChargeDetails &&
                Array.isArray(this.runChargeDetails)
            ) {
                [
                    "left name fee",
                    "left number fee",
                    "left name decor up fee",
                    "left number decor up fee"
                ].forEach((key) => {
                    const isDecorUp = key.includes("decor up");
                    const detail = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes(key) &&
                            (isDecorUp ||
                                !d.name.toLowerCase().includes("decor up"))
                    );
                    if (detail && parseFloat(detail.value || 0) > 0) {
                        let quantity = parseFloat(detail.quantity || 0);
                        if (isDecorUp) {
                            const baseKey = key.replace(
                                " decor up fee",
                                " fee"
                            );
                            const baseFee = this.runChargeDetails.find(
                                (d) =>
                                    d.name &&
                                    d.name.toLowerCase().includes(baseKey) &&
                                    !d.name.toLowerCase().includes("decor up")
                            );
                            quantity = baseFee
                                ? parseFloat(baseFee.quantity || 0)
                                : this.quantity;
                        }
                        let perItemFee =
                            detail.unitPrice != null
                                ? parseFloat(detail.unitPrice)
                                : quantity > 0
                                ? parseFloat(detail.value || 0) / quantity
                                : parseFloat(detail.value || 0);
                        fees.push({
                            name: detail.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: parseFloat(detail.value || 0)
                        });
                    }
                });
            }
            // Right Name/Number Fees (same pattern as Back)
            if (
                locationName === "right" &&
                this.runChargeDetails &&
                Array.isArray(this.runChargeDetails)
            ) {
                [
                    "right name fee",
                    "right number fee",
                    "right name decor up fee",
                    "right number decor up fee"
                ].forEach((key) => {
                    const isDecorUp = key.includes("decor up");
                    const detail = this.runChargeDetails.find(
                        (d) =>
                            d.name &&
                            d.name.toLowerCase().includes(key) &&
                            (isDecorUp ||
                                !d.name.toLowerCase().includes("decor up"))
                    );
                    if (detail && parseFloat(detail.value || 0) > 0) {
                        let quantity = parseFloat(detail.quantity || 0);
                        if (isDecorUp) {
                            const baseKey = key.replace(
                                " decor up fee",
                                " fee"
                            );
                            const baseFee = this.runChargeDetails.find(
                                (d) =>
                                    d.name &&
                                    d.name.toLowerCase().includes(baseKey) &&
                                    !d.name.toLowerCase().includes("decor up")
                            );
                            quantity = baseFee
                                ? parseFloat(baseFee.quantity || 0)
                                : this.quantity;
                        }
                        let perItemFee =
                            detail.unitPrice != null
                                ? parseFloat(detail.unitPrice)
                                : quantity > 0
                                ? parseFloat(detail.value || 0) / quantity
                                : parseFloat(detail.value || 0);
                        fees.push({
                            name: detail.name,
                            perItemFee: perItemFee,
                            quantity: quantity,
                            total: parseFloat(detail.value || 0)
                        });
                    }
                });
            }

            return fees;
        },
        // Get additional locations
        getAdditionalLocations() {
            if (!this.imprintLocations || this.imprintLocations.length <= 1) {
                return [];
            }
            const additionalLocations = [];
            const firstSelectedIndex = this.imprintLocations.findIndex(
                (loc) => loc.is_selected
            );

            for (let i = 0; i < this.imprintLocations.length; i++) {
                const location = this.imprintLocations[i];
                if (
                    location &&
                    location.is_selected &&
                    i !== firstSelectedIndex
                ) {
                    // Check if full color is selected
                    // First check if no_of_color is a number - if it is, it's not full color
                    let isFullColor = false;
                    if (
                        location.no_of_color &&
                        location.no_of_color !== "full_color"
                    ) {
                        const colorNum = parseInt(location.no_of_color);
                        if (!isNaN(colorNum)) {
                            // It's a number, so it's not full color
                            isFullColor = false;
                        } else {
                            // Check other indicators
                            isFullColor =
                                location.is_full_color_selected === true ||
                                location.is_full_color === true;
                        }
                    } else {
                        // no_of_color is "full_color" or other full color indicators
                        isFullColor =
                            location.is_full_color_selected === true ||
                            location.is_full_color === true ||
                            location.no_of_color === "full_color";
                    }
                    const colorCount = isFullColor
                        ? 1
                        : parseInt(location.no_of_color) || 0;
                    if (colorCount > 0 || isFullColor) {
                        additionalLocations.push({
                            index: i,
                            locationName:
                                location.location || `Location ${i + 1}`,
                            colorCount: colorCount,
                            isFullColor: isFullColor,
                            fullColorValue: location.full_color_value,
                            location: location
                        });
                    }
                }
            }
            return additionalLocations;
        },
        // Get location run charge details
        getLocationRunChargeDetails() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return [];
            }

            return this.runChargeDetails
                .filter((detail) => {
                    if (!detail || !detail.name) return false;
                    const nameLower = detail.name.toLowerCase();
                    return (
                        nameLower.includes("location") &&
                        nameLower.includes("run charge")
                    );
                })
                .map((detail) => {
                    const totalValue = parseFloat(detail.value || 0);
                    return {
                        name: detail.name,
                        value: totalValue,
                        perItem: true,
                        perItemFee:
                            this.quantity > 0
                                ? totalValue / this.quantity
                                : totalValue
                    };
                });
        },
        // Get custom color ink and custom color thread fees
        getColorMixFees() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return [];
            }

            return this.runChargeDetails
                .filter((detail) => {
                    if (!detail || !detail.name) return false;
                    const nameLower = detail.name.toLowerCase();
                    return (
                        (nameLower.includes("color ink mix") ||
                            nameLower.includes("color thread mix") ||
                            nameLower.includes("custom color ink fee") ||
                            nameLower.includes("custom color thread fee") ||
                            nameLower.includes("custom color fee")) &&
                        !nameLower.includes("decor up")
                    );
                })
                .map((detail) => {
                    const totalValue = parseFloat(detail.value || 0);
                    return {
                        name: detail.name,
                        value: totalValue,
                        perItem: false, // These are typically flat fees
                        perItemFee: totalValue
                    };
                });
        },
        // Get name/number fees (Front Number, Back Name, Back Number and their Decor Up fees)
        getNameNumberFees() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return [];
            }

            const nameNumberFeeNames = [
                "front number fee",
                "back name fee",
                "back number fee",
                "front number decor up fee",
                "back name decor up fee",
                "back number decor up fee"
            ];

            return this.runChargeDetails
                .filter((detail) => {
                    if (!detail || !detail.name) return false;
                    const nameLower = detail.name.toLowerCase();
                    return nameNumberFeeNames.some((feeName) =>
                        nameLower.includes(feeName)
                    );
                })
                .map((detail) => {
                    // Value is the total fee, calculate per-item fee
                    const totalValue = parseFloat(detail.value || 0);
                    const perItemFee =
                        this.quantity > 0
                            ? totalValue / this.quantity
                            : totalValue;
                    return {
                        name: detail.name,
                        value: totalValue,
                        perItem: true,
                        perItemFee: perItemFee
                    };
                });
        },
        // Get other run charge details (excluding location, color mix, name/number fees)
        getOtherRunChargeDetails() {
            if (
                !this.runChargeDetails ||
                !Array.isArray(this.runChargeDetails)
            ) {
                return [];
            }

            // Check if any location has color upcharge (Specialty Color)
            let hasLocationWiseUpcharge = false;
            // Check if any location has custom color upcharge
            let hasLocationWiseCustomColorUpcharge = false;
            if (this.imprintLocations && this.imprintLocations.length > 0) {
                const firstLocationUpcharge =
                    this.getFirstLocationColorUpcharge();
                if (firstLocationUpcharge > 0) {
                    hasLocationWiseUpcharge = true;
                }
                const firstLocationCustomColorUpcharge =
                    this.getFirstLocationCustomColorUpcharge();
                if (firstLocationCustomColorUpcharge > 0) {
                    hasLocationWiseCustomColorUpcharge = true;
                }
                const additionalLocations = this.getAdditionalLocations();
                if (additionalLocations.length > 0) {
                    additionalLocations.forEach((location) => {
                        if (
                            this.getLocationColorUpcharge(location.location) > 0
                        ) {
                            hasLocationWiseUpcharge = true;
                        }
                        if (
                            this.getLocationCustomColorUpcharge(
                                location.location
                            ) > 0
                        ) {
                            hasLocationWiseCustomColorUpcharge = true;
                        }
                    });
                }
            }

            // Name/Number fees show under 1st Location (Front) and under each Additional Location (Back, Left, Right)
            const excludedPatterns = [
                "location",
                "front number fee",
                "front name fee",
                "back name fee",
                "back number fee",
                "left name fee",
                "left number fee",
                "right name fee",
                "right number fee",
                "front name decor up fee",
                "front number decor up fee",
                "back name decor up fee",
                "back number decor up fee",
                "left name decor up fee",
                "left number decor up fee",
                "right name decor up fee",
                "right number decor up fee"
            ];

            return this.runChargeDetails
                .filter((detail) => {
                    if (!detail || !detail.name) return false;
                    const nameLower = detail.name.toLowerCase();

                    // Exclude Custom Color Ink/Thread Fee - it's shown separately as per-order fee
                    const isMixFee =
                        nameLower.includes("custom color ink") ||
                        nameLower.includes("custom color thread") ||
                        nameLower.includes("color ink mix") ||
                        nameLower.includes("color thread mix");
                    if (isMixFee) {
                        return false; // Exclude - shown separately after additional locations
                    }

                    // Exclude Specialty Color if it's already shown location-wise
                    const isColorUpcharge =
                        nameLower.includes("specialty color") ||
                        nameLower.includes("specialty ink fee");
                    if (isColorUpcharge && hasLocationWiseUpcharge) {
                        return false; // Exclude combined total if location-wise upcharges exist
                    }

                    // Exclude Custom Color if it's already shown location-wise
                    const isCustomColorUpcharge =
                        nameLower.includes("custom color");
                    // Exclude if it's a custom color upcharge (not mix fee) and already shown location-wise
                    if (
                        isCustomColorUpcharge &&
                        hasLocationWiseCustomColorUpcharge &&
                        !nameLower.includes("custom color ink") &&
                        !nameLower.includes("custom color thread") &&
                        !nameLower.includes("color ink mix") &&
                        !nameLower.includes("color thread mix")
                    ) {
                        return false; // Exclude combined total if location-wise upcharges exist
                    }

                    // For decoration type 2, exclude "Location(s) Run Charge" if we have detailed location breakdown
                    // Only show it as a fallback if there's no detailed breakdown available
                    if (
                        (this.decorationType === 2 ||
                            this.decorationType === 4) &&
                        nameLower.includes("location") &&
                        nameLower.includes("run charge")
                    ) {
                        // Check if we have detailed location rows - if yes, exclude the summary
                        const additionalLocations =
                            this.getAdditionalLocations();
                        const hasDetailedBreakdown =
                            additionalLocations.length > 0 &&
                            additionalLocations.some(
                                (loc) => loc.colorCount > 0 || loc.isFullColor
                            );

                        // Only include "Location(s) Run Charge" if there's no detailed breakdown
                        if (hasDetailedBreakdown) {
                            return false; // Exclude summary when detailed breakdown is available
                        }
                        return true; // Include as fallback when no detailed breakdown
                    }

                    return !excludedPatterns.some((pattern) =>
                        nameLower.includes(pattern)
                    );
                })
                .map((detail) => {
                    const totalValue = parseFloat(detail.value || 0);
                    // Check if it's a per-item fee (like Specialty Color or Custom Color)
                    const perItemFees = ["specialty color", "upcharge"];
                    const isPerItem = perItemFees.some((fee) =>
                        detail.name.toLowerCase().includes(fee)
                    );

                    // Pass through quantity and unitPrice when present (e.g. Front Name Fee, Left Name Fee, name/number fees)
                    const hasOwnQuantity =
                        detail.quantity !== undefined &&
                        detail.quantity !== null &&
                        !isNaN(Number(detail.quantity));
                    const qty = hasOwnQuantity ? Number(detail.quantity) : null;
                    const unitPrice =
                        detail.unitPrice !== undefined &&
                        detail.unitPrice !== null &&
                        !isNaN(Number(detail.unitPrice))
                            ? parseFloat(detail.unitPrice)
                            : null;

                    return {
                        name: detail.name,
                        value: totalValue,
                        perItem: isPerItem || hasOwnQuantity,
                        perItemFee:
                            unitPrice != null
                                ? unitPrice
                                : isPerItem && this.quantity > 0
                                ? totalValue / this.quantity
                                : totalValue,
                        quantity: qty,
                        unitPrice: unitPrice
                    };
                });
        },
        // Setup Fee Methods
        // Check if we have data to show custom color fee breakdown
        hasColorMixFeeData() {
            // Check if there's Custom Color Fee data from runChargeDetails
            const mixFeeData = this.getOrderMixFeeData();
            if (mixFeeData && mixFeeData.total > 0) {
                return true;
            }

            // Check if there are roll up id 13 services
            const colorMixFeeServices = this.getServicesGroupedByRollupFee(13);
            if (colorMixFeeServices && colorMixFeeServices.length > 0) {
                return colorMixFeeServices.some(
                    (group) => group.services && group.services.length > 0
                );
            }

            return false;
        },
        hasRunChargeBreakdownDecorationContext() {
            if (this.runChargeDetails && this.runChargeDetails.length > 0) {
                return true;
            }
            const firstLocation = this.getFirstLocationData();
            if (firstLocation) {
                if (this.isFirstLocationFullColor()) {
                    return true;
                }
                if (
                    firstLocation.no_of_color &&
                    parseInt(firstLocation.no_of_color) > 0
                ) {
                    return true;
                }
            }
            const additionalLocations = this.getAdditionalLocations();
            if (additionalLocations.length > 0) {
                return additionalLocations.some(
                    (loc) => loc.isFullColor === true || loc.colorCount > 0
                );
            }
            return false;
        },
        hasRunChargeData() {
            // Embroidery: show tooltip when run charge is calculated but details not yet in runChargeDetails
            if (
                (this.decorationType === 2 ||
                    this.decorationType === 4) &&
                this.calculatedRunChargeTotal > 0
            ) {
                return true;
            }
            return this.hasRunChargeBreakdownDecorationContext();
        },
        hasSetupFeeData() {
            // Check if there are roll up id 2 services (Setup Fee services)
            const setupFeeServices = this.getServicesGroupedByRollupFee(2);
            if (setupFeeServices && setupFeeServices.length > 0) {
                const hasServices = setupFeeServices.some(
                    (group) => group.services && group.services.length > 0
                );
                if (hasServices) {
                    return true;
                }
            }

            if (
                !this.serviceMasterData ||
                Object.keys(this.serviceMasterData).length === 0
            ) {
                return false;
            }
            // Show tooltip if there's at least one location with colors
            const firstLocation = this.getFirstLocationData();
            if (firstLocation) {
                // Check if full color is selected
                if (
                    firstLocation.is_full_color_selected === true ||
                    firstLocation.no_of_color === "full_color"
                ) {
                    return true;
                }
                // Check if regular color count > 0
                if (
                    firstLocation.no_of_color &&
                    parseInt(firstLocation.no_of_color) > 0
                ) {
                    return true;
                }
            }
            return this.getAdditionalLocations().length > 0;
        },
        getServiceFeeStructure() {
            if (!this.serviceMasterData) return null;
            return this.serviceMasterData.fee_structure;
        },
        getServiceMasterData(key) {
            if (!this.serviceMasterData) return 0;
            return parseFloat(this.serviceMasterData[key] || 0);
        },
        getSetupFeeStructure() {
            const decorationType = parseInt(this.decorationType) || 0;
            const useProductSetupSource =
                decorationType === 1 ||
                decorationType === 5 ||
                decorationType === 2 ||
                decorationType === 4;
            if (
                useProductSetupSource &&
                this.activeDecorationService &&
                this.activeDecorationService.product_master_service &&
                this.activeDecorationService.product_master_service
                    .fee_structure !== undefined
            ) {
                return this.activeDecorationService.product_master_service
                    .fee_structure;
            }
            return this.getServiceFeeStructure();
        },
        getSetupFeeMasterData(key) {
            const decorationType = parseInt(this.decorationType) || 0;
            const useProductSetupSource =
                decorationType === 1 ||
                decorationType === 5 ||
                decorationType === 2 ||
                decorationType === 4;
            if (useProductSetupSource) {
                if (
                    this.activeDecorationService &&
                    this.activeDecorationService.product_master_service &&
                    this.activeDecorationService.product_master_service[key] !==
                        undefined
                ) {
                    return (
                        parseFloat(
                            this.activeDecorationService.product_master_service[
                                key
                            ]
                        ) || 0
                    );
                }
                return 0;
            }
            return this.getServiceMasterData(key);
        },
        getLocation1AdditionalColorUnits() {
            const addlColors = this.getFirstLocationAdditionalColors();
            if (addlColors <= 0) return 0;
            return addlColors * this.quantity;
        },
        getLocation2AdditionalColorUnits(colorCount) {
            const decorationType = parseInt(this.decorationType) || 0;
            const count = parseInt(colorCount) || 0;
            if (count <= 0) return 0;
            if (decorationType === 1 || decorationType === 5) {
                // Matches UseImprintCalculator.getLocation2AdditionalColorUnits() for print:
                // bundled price is multiplied by (colorCount * quantity).
                return count * this.quantity;
            }
            if (decorationType === 2 || decorationType === 4) {
                return Math.max(0, count - 1) * this.quantity;
            }
            return count * this.quantity;
        },
        getType1PrintLocation2TierForQty() {
            if (
                !this.decorationPricingData ||
                !this.decorationPricingData.print_location2
            ) {
                return null;
            }

            const qty = this.totalQuantity || this.quantity;
            const priceData = this.decorationPricingData.print_location2;
            let selectedTier = null;

            for (let i = priceData.length - 1; i >= 0; i--) {
                const tier = priceData[i];
                if (
                    tier &&
                    tier.quantity &&
                    parseInt(tier.quantity) <= qty
                ) {
                    selectedTier = tier;
                    break;
                }
            }

            if (!selectedTier && priceData.length > 0) {
                selectedTier = priceData[0];
            }

            return selectedTier;
        },
        getType1AdditionalLocationTemplateColorUnitPrice(colorCount) {
            const tier = this.getType1PrintLocation2TierForQty();
            if (!tier) return 0;
            const count = parseInt(colorCount) || 0;
            if (count <= 0) return 0;
            return parseFloat(tier[`colour${count}`] || 0) || 0;
        },
        getType1SecondLocationAdditionalColorUnitPrice(colorCount) {
            const decorationType = parseInt(this.decorationType) || 0;
            if (decorationType !== 1 && decorationType !== 5) return 0;
            const count = parseInt(colorCount) || 0;
            if (count <= 0) return 0;

            const templateColorN =
                this.getType1AdditionalLocationTemplateColorUnitPrice(count);
            const masterAddl =
                this.getSetupFeeMasterData("cost_per_color_2ndormore") ||
                this.getServiceMasterData("cost_per_color_2ndormore") ||
                0;
            return templateColorN + masterAddl;
        },
        getType1SecondLocationAdditionalColorTotal(location) {
            const decorationType = parseInt(this.decorationType) || 0;
            if (decorationType !== 1 && decorationType !== 5) return 0;
            if (!location || !location.colorCount || location.colorCount <= 0) {
                return 0;
            }
            const unit = this.getType1SecondLocationAdditionalColorUnitPrice(
                location.colorCount
            );
            if (unit <= 0) return 0;
            return (
                unit *
                this.getLocation2AdditionalColorUnits(location.colorCount)
            );
        },
        getEmbroideryLocation2AdditionalColorTotal(location) {
            const decorationType = parseInt(this.decorationType) || 0;
            if (decorationType !== 2 && decorationType !== 4) {
                return 0;
            }
            if (!location || !location.colorCount || location.colorCount <= 0) {
                return 0;
            }

            const addlColorUnit =
                this.getServiceMasterData("cost_per_color_2ndormore") || 0;
            if (addlColorUnit <= 0) return 0;

            const addlCount = location.colorCount;
            return addlColorUnit * addlCount * this.quantity;
        },
        getFirstLocationColorCount() {
            const firstLocation = this.getFirstLocationData();
            if (firstLocation && firstLocation.no_of_color) {
                // Check if full color is selected - if no_of_color is "full_color" or is_full_color is true
                if (
                    firstLocation.no_of_color === "full_color" ||
                    firstLocation.is_full_color === true ||
                    firstLocation.is_full_color_selected === true
                ) {
                    return 1; // Return 1 for full color
                }
                // If no_of_color is a valid number, return it
                const colorNum = parseInt(firstLocation.no_of_color);
                if (!isNaN(colorNum) && colorNum > 0) {
                    return colorNum;
                }
                return 0;
            }
            return 0;
        },
        getLocation1SetupFee() {
            // Check if full color is selected for first location
            if (this.isFirstLocationFullColor()) {
                return this.getSetupFeeMasterData("setup_fee_multicolor") || 0;
            }

            const colorCount = this.getFirstLocationColorCount();
            if (colorCount === 0) return 0;

            const feeStructure = this.getSetupFeeStructure();
            // Handle both string and number comparison
            const feeStructNum = parseInt(feeStructure);
            if (
                feeStructNum === 1 ||
                feeStructure == "1" ||
                feeStructure === "1"
            ) {
                // Per Color: multiply by color count
                return (
                    this.getSetupFeeMasterData("setup_fee_color1") * colorCount
                );
            } else if (
                feeStructNum === 2 ||
                feeStructure == "2" ||
                feeStructure === "2"
            ) {
                // Tiered: each location calculated independently based on its own color count
                if (colorCount === 1) {
                    return this.getSetupFeeMasterData("setup_fee_color1");
                } else {
                    return this.getSetupFeeMasterData("setup_fee_multicolor");
                }
            }
            return 0;
        },
        getAdditionalLocationSetupFee(location) {
            if (!location) return 0;

            // Check if full color is selected for this location
            if (location.isFullColor === true) {
                return this.getSetupFeeMasterData("setup_fee_multicolor") || 0;
            }

            if (location.colorCount === 0) return 0;

            const feeStructure = this.getSetupFeeStructure();
            // Handle both string and number comparison
            const feeStructNum = parseInt(feeStructure);
            if (
                feeStructNum === 1 ||
                feeStructure == "1" ||
                feeStructure === "1"
            ) {
                // Per Color: multiply by color count
                return (
                    this.getSetupFeeMasterData("setup_fee_color1") *
                    location.colorCount
                );
            } else if (
                feeStructNum === 2 ||
                feeStructure == "2" ||
                feeStructure === "2"
            ) {
                // Tiered: each location calculated independently based on its own color count
                if (location.colorCount === 1) {
                    return this.getSetupFeeMasterData("setup_fee_color1");
                } else {
                    return this.getSetupFeeMasterData("setup_fee_multicolor");
                }
            }
            return 0;
        },
        toggleSetupFeeTooltip() {
            this.setupFeeTooltipVisible = !this.setupFeeTooltipVisible;
            this.tooltipVisible = false;
            this.runChargeTooltipVisible = false;
            this.colorMixFeeTooltipVisible = false;
            this.patchTooltipVisible = false;
        },
        toggleColorMixFeeTooltip() {
            this.colorMixFeeTooltipVisible = !this.colorMixFeeTooltipVisible;
            this.tooltipVisible = false;
            this.runChargeTooltipVisible = false;
            this.setupFeeTooltipVisible = false;
            this.patchTooltipVisible = false;
        },
        toggleRushFeeTooltip() {
            this.rushFeeTooltipVisible = !this.rushFeeTooltipVisible;
            this.tooltipVisible = false;
            this.runChargeTooltipVisible = false;
            this.setupFeeTooltipVisible = false;
            this.colorMixFeeTooltipVisible = false;
            this.patchTooltipVisible = false;
        },
        toggleTooltip() {
            this.tooltipVisible = !this.tooltipVisible;
            this.runChargeTooltipVisible = false;
            this.setupFeeTooltipVisible = false;
            this.colorMixFeeTooltipVisible = false;
            this.rushFeeTooltipVisible = false;
            this.patchTooltipVisible = false;
        },
        toggleRunChargeTooltip() {
            this.runChargeTooltipVisible = !this.runChargeTooltipVisible;
            this.tooltipVisible = false;
            this.setupFeeTooltipVisible = false;
            this.colorMixFeeTooltipVisible = false;
            this.patchTooltipVisible = false;
        },
        closeTooltip() {
            this.tooltipVisible = false;
        },
        closeRunChargeTooltip() {
            this.runChargeTooltipVisible = false;
        },
        closeSetupFeeTooltip() {
            this.setupFeeTooltipVisible = false;
        },
        closeColorMixFeeTooltip() {
            this.colorMixFeeTooltipVisible = false;
        },
        closeRushFeeTooltip() {
            this.rushFeeTooltipVisible = false;
        },
        closeTooltipOnClickOutside(event) {
            const wrappers = this.$el.querySelectorAll(".tooltip-wrapper");
            let clickedInsideAnyWrapper = false;

            wrappers.forEach((wrapper) => {
                if (wrapper.contains(event.target)) {
                    clickedInsideAnyWrapper = true;
                }
            });
            if (!clickedInsideAnyWrapper) {
                this.tooltipVisible = false;
                this.runChargeTooltipVisible = false;
                this.setupFeeTooltipVisible = false;
                this.colorMixFeeTooltipVisible = false;
                this.rushFeeTooltipVisible = false;
                this.patchTooltipVisible = false;
            }
        }
    },
    mounted() {
        document.addEventListener("click", this.closeTooltipOnClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeTooltipOnClickOutside);
    }
};
</script>

<style scoped>
.cost-breakdown-container {
    width: 100%;
    max-width: 100%;
    border: 1px solid #e0e0e0;
    padding: 10px 15px;
    border-radius: 8px;
    background-color: #f9f9f9;
}
.cost-breakdown-container .tooltip-content {
    left: auto;
    right: 0;
    transform: none;
}
@media (max-width: 767px) {
    .cost-breakdown-container {
        margin-top: 1.5rem;
        max-width: 100%;
    }
}

.ml-auto {
    margin-left: auto;
}

.cost-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    gap: 8px;
    min-width: 0;
}

.cost-label {
    font-size: 16px;
    line-height: 16px;
    color: #2b2b2b;
    font-weight: 400;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cost-value {
    font-size: 16px;
    line-height: 16px;
    font-weight: 400;
    color: #000000;
    flex-shrink: 0;
}

.text-danger {
    color: #dc3545 !important;
}

.cost-separator {
    margin: 8px 0;
    border-top: 1px solid #ddd;
}

.total-row {
    padding-top: 8px;
}

.cost-label-total {
    font-size: 16px;
    line-height: 16px;
    color: #2b2b2b;
    font-weight: 400;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cost-value-total {
    font-size: 16px;
    line-height: 16px;
    font-weight: 600;
    color: #000000;
    flex-shrink: 0;
}
.tooltip-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.tooltip-wrapper .cost-label {
    flex: 1 1 auto;
    min-width: 0;
}

.info-icon-svg {
    width: 16px;
    height: 16px;
    margin-left: 5px;
    color: #888;
    transition: color 0.2s;
}

.tooltip-wrapper:hover .info-icon-svg {
    color: #333;
}

/* --- TOOLTIP STYLES --- */
.tooltip-content {
    visibility: hidden;
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50% !important;
    transform: translateX(-50%);
    background: #fff;
    color: hsla(0, 0%, 7%, 1);
    padding: 0px;
    border-radius: 6px;
    z-index: 99;
    min-width: 392px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border: 1px solid #ddd;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 14px;
}

.tooltip-content::after {
    content: none;
}

.tooltip-content.is-visible {
    visibility: visible;
    opacity: 1;
}

.tooltip-header {
    padding: 9px 16px;
    background: hsla(204, 63%, 94%, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.tooltip-header h4 {
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: hsla(209, 72%, 41%, 1);
    margin-bottom: 0px;
}

.tooltip-header .close-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    color: #666;
    flex-shrink: 0;
    transition: color 0.2s ease;
}

.tooltip-header .close-icon:hover {
    color: #333;
}

.tooltip-body {
    white-space: normal;
    padding: 16px;
}
.tooltip-body .tooltip-subsection-title {
    margin-top: 12px;
    margin-bottom: 6px;
    font-weight: 600;
    font-size: 0.95em;
}

.tooltip-body ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.tooltip-body li {
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    font-size: 14px;
}

.tooltip-body li:last-child {
    margin-bottom: 0;
}
.tooltip-body li.gray-out {
    color: #999;
}

.tooltip-body li.gray-out span:last-child {
    color: #999;
}

/* Table styles matching PCNAPriceBreakdown */
.base-fee-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
}

.base-fee-table thead {
    background-color: #f5f5f5;
}

.base-fee-table th {
    padding: 8px;
    text-align: left;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #ddd;
}

.base-fee-table th:nth-child(2),
.base-fee-table th:nth-child(3),
.base-fee-table th:nth-child(4) {
    text-align: right;
}

.base-fee-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
}

.base-fee-table td:nth-child(2),
.base-fee-table td:nth-child(3),
.base-fee-table td:nth-child(4) {
    text-align: right;
}

.base-fee-table tbody tr.disable_data {
    color: #666;
}

.base-fee-table tbody tr.total-row {
    background-color: #f9f9f9;
    font-weight: 600;
}

.base-fee-table tbody tr.total-row td {
    border-top: 2px solid #ddd;
    padding-top: 10px;
}

.base-fee-table tbody tr.rollup-fee-header {
    background-color: #e8f4f8;
    font-weight: 600;
}

.base-fee-table tbody tr.rollup-fee-header td {
    padding-top: 12px;
    padding-bottom: 8px;
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
    color: #2c5282;
}

.color-box-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
}

.color-box {
    width: 20px;
    height: 20px;
    border: 1px solid #ddd;
    border-radius: 3px;
    cursor: pointer;
    display: inline-block;
}

.color-box:hover {
    border-color: #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.product-base-price-row {
    font-weight: bold;
}
.product_details_update_v3 .tooltip-body {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 12px;
    max-height: 200px;
    overflow-y: auto;
}
</style>
